import { getWrestlerName, getWrestlerNameUpper, wrestlerMatchesSearch, sortWrestlersByName } from './wrestlerUtils';
import STORYLINES from './storylinesDatabase';
import React, { useState } from 'react';
import { MATCH_TYPES, MATCH_RULES, SLOT_LABELS } from './matchTypesDatabase';
import { PROMO_ACTIONS } from './promoDatabase';

// ── Assets / Helpers ─────────────────────────────────────────────────
const placeholder = '/wrestlers/placeholder.png';

const getWrestlerRender = (w) => {
  if (!w) return null;
  if (w.customRender) return w.customRender;
  if (w.wrestler_id) return process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png';
  return null;
};

const getTitleRender = (id) => id ? process.env.PUBLIC_URL + '/titles/' + id + '-render.png' : null;
const titleRenderError = process.env.PUBLIC_URL + '/titles/title-error-render.png';
const wrestlerRenderError = process.env.PUBLIC_URL + '/wrestlers/render_error.png';
const onTitleRenderError = (e) => { if (e.target.src !== titleRenderError) e.target.src = titleRenderError; };
const onWrestlerRenderError = (e) => { if (e.target.src !== wrestlerRenderError) e.target.src = wrestlerRenderError; };

const STAR_FULL = process.env.PUBLIC_URL + '/icons/star-full.png';
const STAR_HALF = process.env.PUBLIC_URL + '/icons/star-half.png';

// ── Rating config ────────────────────────────────────────────────────
const RATING_LABELS = {
  0.5: 'DUD', 1: 'Poor', 1.5: 'Below Average', 2: 'Average',
  2.5: 'Decent', 3: 'Good', 3.5: 'Very Good', 4: 'Great',
  4.5: 'Excellent', 5: 'Classic',
};

const RATING_COLORS = {
  0.5: '#888', 1: '#e74c3c', 1.5: '#e67e22', 2: '#f39c12',
  2.5: '#f1c40f', 3: '#2ecc71', 3.5: '#27ae60', 4: '#3498db',
  4.5: '#9b59b6', 5: '#d4af37',
};

// ── Shared: ALL match rules → team sizes ─────────────────────────────
export const ALL_RULE_TEAMS = {
  '1v1': [1,1], 'tag': [2,2], 'triple-threat': [1,1,1], 'fatal-4-way': [1,1,1,1],
  'five-way': [1,1,1,1,1], 'six-way': [1,1,1,1,1,1], '3v3': [3,3],
  'tag-triple-threat': [2,2,2], 'eight-way': [1,1,1,1,1,1,1,1], '4v4': [4,4],
  'tag-fatal-4-way': [2,2,2,2], '1v2': [1,2], '1v3': [1,3], '2v3': [2,3],
};

// Get display name for any participant — handles brand sentinels
const getParticipantDisplayName = (p) => {
  if (!p) return '?';
  if (p.isBrand) return p.brand_id || p.name || '?';
  return getWrestlerName(p, 'display') || p.name || '?';
};

// Renders match participants grouped by team with & within teams and vs between teams.
// Optionally highlights the winning team/player.
// Returns an array of React spans — use inside a <span> or <div>.
export const renderMatchParticipants = (participants, matchRule, winner, S_mono) => {
  var validP = (participants || []).filter(Boolean);
  if (validP.length === 0) return [<span key="empty" style={{ color: 'rgba(255,255,255,0.25)' }}>—</span>];
  var teamSizes = ALL_RULE_TEAMS[matchRule] || null;
  var teams;
  if (teamSizes) {
    teams = [];
    var off = 0;
    teamSizes.forEach(function(sz) { teams.push(validP.slice(off, off + sz)); off += sz; });
  } else {
    // Unknown rule — each participant is its own "team"
    teams = validP.map(function(p) { return [p]; });
  }
  return teams.map(function(team, ti) {
    var teamWon = winner && team.some(function(p) {
      if (!p) return false;
      return String(p.wrestler_id) === String(winner.wrestler_id) ||
             (p.isBrand && winner.isBrand && p.brand_id === winner.brand_id);
    });
    return (
      <span key={ti}>
        {ti > 0 && <span style={{ color: '#e74c3c', margin: '0 4px' }}>vs</span>}
        {team.map(function(p, pi) {
          var name = getParticipantDisplayName(p);
          var highlighted = teamWon;
          return (
            <span key={pi}>
              {pi > 0 && <span style={{ color: 'rgba(255,255,255,0.35)', margin: '0 2px' }}>&amp;</span>}
              <span style={{ fontFamily: "'Monda',sans-serif", color: highlighted ? '#fff' : 'rgba(255,255,255,0.55)', fontWeight: highlighted ? 'bold' : 'normal' }}>
                {highlighted && pi === 0 ? '👑 ' : ''}{name}
              </span>
            </span>
          );
        })}
      </span>
    );
  });
};

const StarRating = ({ value, onChange, size = 24 }) => {
  const [hovered, setHovered] = useState(null);
  const display = hovered || value || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
      <div style={{ display: 'flex', gap: '2px' }}>
        {[1,2,3,4,5].map(star => {
          const full = display >= star;
          const half = !full && display >= star - 0.5;
          return (
            <div key={star} style={{ position: 'relative', width: size, height: size, cursor: 'pointer' }}
              onMouseLeave={() => setHovered(null)}>
              {/* Half left */}
              <div style={{ position: 'absolute', left: 0, top: 0, width: '50%', height: '100%', zIndex: 2 }}
                onMouseEnter={() => setHovered(star - 0.5)}
                onClick={() => onChange(star - 0.5)} />
              {/* Full right */}
              <div style={{ position: 'absolute', right: 0, top: 0, width: '50%', height: '100%', zIndex: 2 }}
                onMouseEnter={() => setHovered(star)}
                onClick={() => onChange(star)} />
              <img loading="lazy" src={full ? STAR_FULL : half ? STAR_HALF : STAR_FULL} alt=""
                style={{ width: size, height: size, objectFit: 'contain', opacity: (full || half) ? 1 : 0.15, filter: (full || half) ? 'drop-shadow(0 0 4px ' + (RATING_COLORS[display] || '#d4af37') + ')' : 'none' }} />
            </div>
          );
        })}
      </div>
      {value > 0 && (
        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: RATING_COLORS[value] || '#d4af37', letterSpacing: '1px', fontWeight: 'bold' }}>
          {value} ★ — {RATING_LABELS[value] || ''}
        </div>
      )}

    </div>
  );
};

const css = `
  * { box-sizing: border-box; }
  html { font-size: 133%; }
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 3px; }
  .sr-row:hover { background: rgba(255,255,255,0.04) !important; }
  .sr-btn:hover { background: rgba(255,255,255,0.1) !important; border-color: rgba(255,255,255,0.6) !important; color: #fff !important; }
`;

const S = {
  panel: { background: 'rgba(8,8,8,0.92)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '10px' },
  anton: { fontFamily: "'Anton',sans-serif" },
  mono: { fontFamily: "'Monda',sans-serif" },
  label: { fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' },
};


// ── PLE Common Match Result (Opener / Main Event) ────────────────────
export const PLECommonMatchResult = ({ label, slotColor, brands, myBrand, myParticipants, matchRule, matchType, brandColor, onSave, onBack, onCancel }) => {
  const [winnerTeamIdx, setWinnerTeamIdx] = useState(null);
  const [notes, setNotes] = useState('');
  const [rating, setRating] = useState(0);

  const RULE_TEAMS_SR = {
    '1v1': [1,1], 'tag': [2,2], 'triple-threat': [1,1,1], 'fatal-4-way': [1,1,1,1],
    'five-way': [1,1,1,1,1], 'six-way': [1,1,1,1,1,1], '3v3': [3,3],
    'tag-triple-threat': [2,2,2], 'eight-way': [1,1,1,1,1,1,1,1], '4v4': [4,4],
    'tag-fatal-4-way': [2,2,2,2], '1v2': [1,2], '1v3': [1,3], '2v3': [2,3],
  };

  var BRAND_LOGOS_SR = {
    'Raw': process.env.PUBLIC_URL + '/brands/logos/RAW25_LOGO.png',
    'SmackDown': process.env.PUBLIC_URL + '/brands/logos/SMACKDOWN25_LOGO.png',
    'NXT': process.env.PUBLIC_URL + '/brands/logos/NXT_LOGO.png',
    'NXT 2.0': process.env.PUBLIC_URL + '/brands/logos/NXT20_LOGO.png',
    'WCW': process.env.PUBLIC_URL + '/brands/logos/WCW_LOGO.png',
    'ECW': process.env.PUBLIC_URL + '/brands/logos/ECW_LOGO.png',
    'NXT Mutiny': process.env.PUBLIC_URL + '/brands/logos/NXTMUTINY_LOGO.png',
  };
  var BRAND_COLORS_SR = { 'Raw': '#c0392b', 'SmackDown': '#1a6bb5', 'NXT': '#a8a9ad', 'NXT 2.0': '#e91e8c', 'ECW': '#7b1fa2', 'WCW': '#e65100', 'NXT Mutiny': '#424242' };

  // Group participants into teams based on matchRule
  var effectiveRule = matchRule || '1v1';
  var teamSizes = RULE_TEAMS_SR[effectiveRule] || [1, 1];
  var allP = (myParticipants || []).filter(Boolean);
  var teams = [];
  var tIdx = 0;
  teamSizes.forEach(function(size) {
    teams.push(allP.slice(tIdx, tIdx + size));
    tIdx += size;
  });
  // If no participants defined, each brand gets its own solo slot
  if (allP.length === 0 && brands && brands.length > 0) {
    teams = brands.map(function(b) { return [{ isBrand: true, brand_id: b.name, name: b.name, brandColor: b.color, brandLogo: b.logo }]; });
  }

  var canSave = winnerTeamIdx !== null;

  // Build team display name from members
  var getTeamName = function(team) {
    return team.map(function(p) {
      return p.isBrand ? (p.brand_id || p.name) : (p.name || '?');
    }).join(' & ');
  };

  // Team accent color: first brand color in the team, or a superstar's brand color
  var getTeamColor = function(team) {
    for (var i = 0; i < team.length; i++) {
      var p = team[i];
      if (p.isBrand) return p.brandColor || BRAND_COLORS_SR[p.brand_id] || '#888';
    }
    return '#888';
  };

  // Does this team contain any member from myBrand?
  var teamContainsMyBrand = function(team) {
    return team.some(function(p) {
      if (p.isBrand) return p.brand_id === myBrand;
      return true; // superstars belong to myBrand
    });
  };

  return (
    <div style={{ position: 'fixed', top: '91px', left: 0, right: 0, bottom: 0, zIndex: 700, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', background: 'rgba(0,0,0,0.88)', padding: '51px 16px 16px' }}>
      <div style={{ width: '100%', maxWidth: '580px', background: 'rgba(6,4,0,0.97)', border: '1px solid ' + (slotColor || '#d4af37') + '44', borderRadius: '14px', display: 'flex', flexDirection: 'column', maxHeight: '100%', overflow: 'hidden' }}>
        <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, ' + (slotColor || '#d4af37') + ', transparent)' }} />
        {/* Header */}
        <div style={{ padding: '16px 22px 12px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: slotColor || '#d4af37', letterSpacing: '2px', marginBottom: '3px' }}>PLE — INTER-BRAND</div>
            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.95rem', letterSpacing: '2px', color: '#fff' }}>{label}</div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginTop: '2px' }}>
              {effectiveRule !== '1v1' ? effectiveRule.toUpperCase() : '1 VS 1'}
              {matchType && matchType !== 'normal' ? ' · ' + matchType.toUpperCase() : ''}
            </div>
          </div>
          {onCancel && <button onClick={onCancel} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontFamily: "'Monda',sans-serif", fontSize: '0.44rem' }}>✕ CANCEL</button>}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Teams / Winner selection */}
          <div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1.5px', marginBottom: '10px' }}>WINNING TEAM</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {teams.map(function(team, ti) {
                var isSelected = winnerTeamIdx === ti;
                var tColor = getTeamColor(team);
                var isMe = teamContainsMyBrand(team);
                return (
                  <div key={ti} onClick={function() { setWinnerTeamIdx(ti); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px', borderRadius: '8px', border: '2px solid ' + (isSelected ? tColor : 'rgba(255,255,255,0.08)'), background: isSelected ? tColor + '18' : 'rgba(255,255,255,0.02)', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s' }}>
                    {/* Check circle */}
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', border: '2px solid ' + (isSelected ? tColor : 'rgba(255,255,255,0.2)'), background: isSelected ? tColor : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {isSelected && <span style={{ color: '#000', fontSize: '0.5rem', fontWeight: 'bold' }}>✓</span>}
                    </div>
                    {/* Team members */}
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {team.map(function(p, pi) {
                        var isBrand = p.isBrand;
                        var pColor = isBrand ? (p.brandColor || BRAND_COLORS_SR[p.brand_id] || '#888') : null;
                        var pLogo  = isBrand ? (p.brandLogo  || BRAND_LOGOS_SR[p.brand_id]  || null) : null;
                        var displayName = isBrand ? (p.brand_id || p.name) : (p.name || '?');
                        return (
                          <React.Fragment key={pi}>
                            {pi > 0 && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: isSelected ? tColor + 'aa' : 'rgba(255,255,255,0.25)' }}>&amp;</span>}
                            {isBrand ? (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                {pLogo
                                  ? <img loading="lazy" src={pLogo} alt={displayName}
                                      style={{ height: '22px', objectFit: 'contain', filter: isSelected ? 'drop-shadow(0 0 5px ' + pColor + '99)' : 'none', opacity: isSelected ? 1 : 0.6, transition: 'filter 0.15s, opacity 0.15s' }}
                                      onError={onTitleRenderError} />
                                  : <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.65rem', color: isSelected ? pColor : (pColor + '88'), letterSpacing: '1px' }}>{displayName}</span>
                                }
                              </span>
                            ) : (
                              <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.52rem', color: isSelected ? '#fff' : 'rgba(255,255,255,0.6)', letterSpacing: '0.5px' }}>{displayName}</span>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                    {/* "YOUR BRAND" indicator */}
                    {isMe && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: isSelected ? tColor : 'rgba(255,255,255,0.25)', letterSpacing: '0.5px', flexShrink: 0, border: '1px solid ' + (isSelected ? tColor + '55' : 'rgba(255,255,255,0.1)'), borderRadius: '3px', padding: '1px 6px' }}>YOUR BRAND</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rating */}
          <div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1.5px', marginBottom: '8px' }}>MATCH RATING</div>
            <StarRating value={rating} onChange={setRating} size={24} />
          </div>

          {/* Notes */}
          <div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1.5px', marginBottom: '6px' }}>NOTES</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '8px 12px', color: '#fff', fontSize: '0.55rem', fontFamily: "'Monda',sans-serif", outline: 'none', resize: 'vertical', minHeight: '60px' }}
              placeholder="Notes..." />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 22px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {onBack
            ? <button onClick={onBack} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '8px 14px', borderRadius: '4px', cursor: 'pointer' }}>← BACK</button>
            : <div />}
          <button onClick={function() {
              if (!canSave) return;
              var winnerTeam = teams[winnerTeamIdx] || [];
              // winnerBrand = first brand in the winning team (for ShowClosingScreen compat)
              var firstBrand = winnerTeam.find(function(p) { return p.isBrand; });
              var winnerBrand = firstBrand ? firstBrand.brand_id : myBrand;
              var myWon = teamContainsMyBrand(winnerTeam);
              onSave({ winnerTeamIdx, winnerTeam, winnerBrand, myWon, notes, rating });
            }}
            disabled={!canSave}
            style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.7rem', letterSpacing: '2px', background: canSave ? (slotColor || '#d4af37') + '18' : 'transparent', border: '1px solid ' + (canSave ? (slotColor || '#d4af37') : 'rgba(255,255,255,0.12)'), color: canSave ? (slotColor || '#d4af37') : 'rgba(255,255,255,0.2)', padding: '9px 24px', borderRadius: '6px', cursor: canSave ? 'pointer' : 'not-allowed' }}>
            CONFIRM ▶
          </button>
        </div>
      </div>
    </div>
  );
};


// ── Promo Change Card ─────────────────────────────────────────────────
const getClassIcon = (cls) => process.env.PUBLIC_URL + '/icons/class-' + (cls || '').toLowerCase() + '.png';
const getRoleIcon = (heel) => process.env.PUBLIC_URL + '/icons/role-' + (heel ? 'heel' : 'face') + '.png';

export const PromoChangeCard = ({ slot, brandColor, brandBg, onContinue }) => {
  const booking = slot && slot.booking;
  const promoAction = booking && booking.promoAction;
  const participants = (booking && booking.participants) || [];
  const w = participants[0];

  if (!w || (promoAction !== 'role-change' && promoAction !== 'class-change')) return null;

  const isRoleChange = promoAction === 'role-change';
  const isClassChange = promoAction === 'class-change';

  // For role change: heel flips
  const wasHeel = w.heel;
  const nowHeel = !wasHeel;

  // For class change: classes swap
  const wasClass = w.fight_class;
  const nowClass = w.fight_class_secondary || w.fight_class;

  const accentColor = isRoleChange ? '#9b59b6' : '#e67e22';
  const label = isRoleChange ? 'ROLE CHANGE' : 'CLASS CHANGE';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 750, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onContinue}>
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideChange { from { opacity: 0; transform: scale(0.8); } to { opacity: 1; transform: scale(1); } }
        .change-card { animation: fadeInUp 0.4s ease forwards; }
        .change-icons { animation: slideChange 0.5s ease 0.3s both; }
      `}</style>
      {brandBg && <img loading="lazy" src={brandBg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(18px) brightness(0.18)', zIndex: 0 }} />}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1 }} />

      <div className="change-card" style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', padding: '40px 32px', maxWidth: '440px', width: '100%' }}>

        {/* Label */}
        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.52rem', letterSpacing: '3px', color: accentColor, background: accentColor + '18', border: '1px solid ' + accentColor + '55', borderRadius: '4px', padding: '4px 16px' }}>
          {label}
        </div>

        {/* Wrestler render */}
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, ' + accentColor + '33 0%, transparent 70%)', borderRadius: '50%' }} />
          <img loading="lazy" src={getWrestlerRender(w) || (process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png')} alt={w.name}
            style={{ height: '220px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.9))', position: 'relative', zIndex: 1 }}
            onError={e => { e.target.style.display='none'; }} />
        </div>

        {/* Name */}
        <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.4rem', letterSpacing: '3px', color: '#fff', textAlign: 'center' }}>
          {getWrestlerNameUpper(w, 'display')}
        </div>

        {/* Change display */}
        <div className="change-icons" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          {isRoleChange && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <img loading="lazy" src={getRoleIcon(wasHeel)} alt="" style={{ width: '52px', height: '52px', objectFit: 'contain', opacity: 0.5, filter: 'grayscale(1)' }} onError={e => { e.target.style.display='none'; }} />
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.48rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px' }}>{wasHeel ? 'HEEL' : 'FACE'}</div>
              </div>
              <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.8rem', color: accentColor }}>→</div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <img loading="lazy" src={getRoleIcon(nowHeel)} alt="" style={{ width: '52px', height: '52px', objectFit: 'contain', filter: 'drop-shadow(0 0 12px ' + accentColor + '88)' }} onError={e => { e.target.style.display='none'; }} />
                <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.55rem', color: accentColor, letterSpacing: '1px' }}>{nowHeel ? 'HEEL' : 'FACE'}</div>
              </div>
            </>
          )}
          {isClassChange && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <img loading="lazy" src={getClassIcon(wasClass)} alt="" style={{ width: '52px', height: '52px', objectFit: 'contain', opacity: 0.5, filter: 'grayscale(1)' }} onError={e => { e.target.style.display='none'; }} />
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.48rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px' }}>{wasClass}</div>
              </div>
              <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.8rem', color: accentColor }}>→</div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <img loading="lazy" src={getClassIcon(nowClass)} alt="" style={{ width: '52px', height: '52px', objectFit: 'contain', filter: 'drop-shadow(0 0 12px ' + accentColor + '88)' }} onError={e => { e.target.style.display='none'; }} />
                <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.55rem', color: accentColor, letterSpacing: '1px' }}>{nowClass}</div>
              </div>
            </>
          )}
        </div>

        {/* Click to continue */}
        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', marginTop: '8px' }}>
          CLICK TO CONTINUE
        </div>
      </div>
    </div>
  );
};


// ── Title Change Card ────────────────────────────────────────────────
export const TitleChangeCard = ({ result, gameData, brandColor, brandBg, onContinue }) => {
  if (!result || !result.titleChange || !result.newChampion) return null;

  var champions = (gameData && gameData.champions) || {};
  var champSlots = champions.slots || [];
  var champMap = champions.champions || {};
  var slotData = champSlots.find(function(s) { return s.key === result.titleSlotKey; });
  var titleId = slotData && slotData.titleOverride && slotData.titleOverride.id;
  var titleName = (slotData && slotData.titleOverride && slotData.titleOverride.name) || result.titleSlotKey || 'CHAMPIONSHIP';
  var storedRender = slotData && slotData.titleOverride && slotData.titleOverride.render;
  var titleRender = storedRender || (titleId ? process.env.PUBLIC_URL + '/titles/' + titleId + '-render.png' : null);

  // Old champion(s)
  var oldChamps = (champMap[result.titleSlotKey] || []).filter(Boolean);
  var newChamp = result.newChampion;
  var roster = (gameData && gameData.roster) || [];
  var newChampFull = roster.find(function(w) { return w.wrestler_id === newChamp.wrestler_id; }) || newChamp;
  var isTagTitle = slotData && slotData.isTagTeam;
  var tagTeams = (gameData && gameData.tagTeams) || [];
  // For tag titles: find the full tag team of the new champion
  var newChampsTeam = [newChampFull];
  if (isTagTitle) {
    // First try: find a registered tag team
    var winnerTeam = tagTeams.find(function(t) {
      return (t.partner1 && String(t.partner1.wrestler_id) === String(newChamp.wrestler_id)) ||
             (t.partner2 && String(t.partner2.wrestler_id) === String(newChamp.wrestler_id));
    });
    if (winnerTeam) {
      var partner = String(winnerTeam.partner1.wrestler_id) === String(newChamp.wrestler_id) ? winnerTeam.partner2 : winnerTeam.partner1;
      var partnerFull = roster.find(function(w) { return String(w.wrestler_id) === String(partner.wrestler_id); }) || partner;
      newChampsTeam = [newChampFull, partnerFull];
    } else {
      // Fallback: find the partner from match participants (same team side)
      var matchParts = (result.participants || []).filter(Boolean);
      var matchRule = result.matchRule || '';
      var TAG_RULE_TEAMS_TC = {
        'tag': [2,2], '3v3': [3,3], '4v4': [4,4],
        'tag-triple-threat': [2,2,2], 'tag-fatal-4-way': [2,2,2,2],
        '2v2': [2,2], '1v2': [1,2], '1v3': [1,3], '2v3': [2,3],
      };
      var teamSizesTC = TAG_RULE_TEAMS_TC[matchRule];
      if (teamSizesTC && matchParts.length > 0) {
        // Group by teams using rule, then find winner's side
        var teamsTC = [], offsetTC = 0;
        teamSizesTC.forEach(function(sz) { teamsTC.push(matchParts.slice(offsetTC, offsetTC + sz)); offsetTC += sz; });
        var winnerSide = teamsTC.find(function(team) {
          return team.some(function(w) { return w && String(w.wrestler_id) === String(newChamp.wrestler_id); });
        });
        if (winnerSide && winnerSide.length > 1) {
          newChampsTeam = winnerSide.map(function(w) {
            return roster.find(function(r) { return String(r.wrestler_id) === String(w.wrestler_id); }) || w;
          });
        }
      } else if (matchParts.length >= 2) {
        // No matchRule saved — assume standard 2-person teams (tag match)
        // Participants are ordered: [team1p1, team1p2, team2p1, team2p2, ...]
        // Winner's partner is at the adjacent even/odd index
        var winnerIdx = matchParts.findIndex(function(w) { return w && String(w.wrestler_id) === String(newChamp.wrestler_id); });
        if (winnerIdx !== -1) {
          var partnerIdx = winnerIdx % 2 === 0 ? winnerIdx + 1 : winnerIdx - 1;
          var tagPartner = matchParts[partnerIdx];
          if (tagPartner) {
            var tagPartnerFull = roster.find(function(r) { return String(r.wrestler_id) === String(tagPartner.wrestler_id); }) || tagPartner;
            newChampsTeam = winnerIdx % 2 === 0 ? [newChampFull, tagPartnerFull] : [tagPartnerFull, newChampFull];
          }
        }
      }
    }
  }
  var getR = function(w) {
    if (!w) return null;
    if (w.customRender) return w.customRender;
    return w.wrestler_id ? process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png' : null;
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 750, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onContinue}>
      <style>{`
        @keyframes titleFadeIn { from { opacity: 0; transform: translateY(40px) scale(0.9); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes titleGlow { 0%,100% { filter: drop-shadow(0 0 20px rgba(212,175,55,0.9)) drop-shadow(0 0 40px rgba(212,175,55,0.5)); } 50% { filter: drop-shadow(0 0 30px rgba(212,175,55,1)) drop-shadow(0 0 60px rgba(212,175,55,0.8)); } }
        @keyframes champSlide { from { opacity: 0; transform: translateX(-30px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes newChampSlide { from { opacity: 0; transform: translateX(30px); } to { opacity: 1; transform: translateX(0); } }
        .tc-main { animation: titleFadeIn 0.5s ease forwards; }
        .tc-belt { animation: titleGlow 2s ease-in-out infinite; }
        .tc-old { animation: champSlide 0.4s ease 0.3s both; }
        .tc-new { animation: newChampSlide 0.4s ease 0.5s both; }
        .tc-arrow { animation: titleFadeIn 0.3s ease 0.4s both; }
      `}</style>
      {brandBg && <img loading="lazy" src={brandBg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(18px) brightness(0.18)', zIndex: 0 }} />}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.15) 0%, transparent 65%)', zIndex: 1 }} />

      <div className="tc-main" style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', padding: '40px 32px', maxWidth: '600px', width: '100%' }}>

        {/* NEW CHAMPION badge */}
        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.52rem', letterSpacing: '4px', color: '#d4af37', background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.5)', borderRadius: '4px', padding: '4px 20px' }}>
          NEW CHAMPION
        </div>

        {/* Title belt */}
        {titleRender && (
          <img loading="lazy" className="tc-belt" src={titleRender} alt={titleName}
            style={{ height: '90px', objectFit: 'contain' }}
            onError={function(e) { e.target.style.display='none'; }} />
        )}

        {/* Title name */}
        <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.2rem', letterSpacing: '3px', color: '#d4af37', textAlign: 'center', textShadow: '0 0 20px rgba(212,175,55,0.8)' }}>
          {titleName.toUpperCase()}
        </div>

        {/* Champions transition */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '32px', width: '100%', justifyContent: 'center' }}>

          {/* Old champions */}
          <div className="tc-old" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', opacity: 0.5 }}>
            {oldChamps.length > 0 ? (
              <React.Fragment>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', justifyContent: 'center' }}>
                  {oldChamps.map(function(c, i) {
                    var cFull = roster.find(function(w) { return w.wrestler_id === c.wrestler_id; }) || c;
                    return (
                      <img loading="lazy" key={i} src={getR(cFull)} alt={cFull.name}
                        style={{ height: '173px', width: 'auto', maxWidth: '138px', objectFit: 'contain', objectPosition: 'top', filter: 'grayscale(1)' }}
                        onError={function(e) { e.target.style.display='none'; }} />
                    );
                  })}
                </div>
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                  {oldChamps.map(function(c) { var found = roster.find(function(w) { return w.wrestler_id === c.wrestler_id; }) || c; return getWrestlerNameUpper(found, 'display'); }).join(' & ')}
                </div>
              </React.Fragment>
            ) : (
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>VACANT</div>
            )}
          </div>

          {/* Arrow */}
          <div className="tc-arrow" style={{ fontFamily: "'Anton',sans-serif", fontSize: '2.5rem', color: '#d4af37', textShadow: '0 0 20px rgba(212,175,55,0.8)', flexShrink: 0 }}>→</div>

          {/* New champion(s) */}
          <div className="tc-new" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', justifyContent: 'center' }}>
              {newChampsTeam.map(function(w, i) { return (
                <img loading="lazy" key={i} src={getR(w)} alt={w.name}
                  style={{ height: '207px', width: 'auto', maxWidth: '150px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 0 20px rgba(212,175,55,0.9))' }}
                  onError={function(e) { e.target.style.display='none'; }} />
              ); })}
            </div>
            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.65rem', letterSpacing: '2px', color: '#d4af37', textShadow: '0 0 12px rgba(212,175,55,0.8)', textAlign: 'center' }}>
              👑 {newChampsTeam.map(function(w) { return getWrestlerNameUpper(w, 'display'); }).join(' & ')}
            </div>
          </div>
        </div>

        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', marginTop: '8px' }}>
          CLICK TO CONTINUE
        </div>
      </div>
    </div>
  );
};

// ── Rivalry Created Card ──────────────────────────────────────────────
export const RivalryCreatedCard = ({ rivalry, roster, brandColor, brandBg, onContinue }) => {
  if (!rivalry) return null;

  var RIVALRY_LEVEL_COLORS_RC = { 1: '#3498db', 2: '#e67e22', 3: '#e74c3c', 4: '#d4af37' };
  var accentColor = RIVALRY_LEVEL_COLORS_RC[rivalry.level] || '#e74c3c';
  var getSide = function(ids) {
    return (ids || []).map(function(id) {
      return (roster || []).find(function(w) { return String(w.wrestler_id) === String(id); });
    }).filter(Boolean);
  };
  var side1 = getSide(rivalry.side1);
  var side2 = getSide(rivalry.side2);
  var getR = function(w) {
    if (!w) return null;
    if (w.customRender) return w.customRender;
    return w.wrestler_id ? process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png' : null;
  };

  var renderSide = function(members, animClass) {
    return (
      <div className={animClass} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', flex: 1 }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', justifyContent: 'center' }}>
          {members.map(function(w, i) { return (
            <img loading="lazy" key={i} src={getR(w)} alt={w.name}
              style={{ height: '219px', width: 'auto', maxWidth: '161px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.9))' }}
              onError={function(e) { e.target.style.display='none'; }} />
          ); })}
        </div>
        <div style={{ textAlign: 'center' }}>
          {members.map(function(w, i) { return (
            <div key={i} style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.55rem', letterSpacing: '1.5px', color: '#fff', whiteSpace: 'nowrap' }}>{getWrestlerNameUpper(w, 'display')}</div>
          ); })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 750, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onContinue}>
      <style>{`
        @keyframes rivalryIn { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }
        @keyframes side1In { from { opacity: 0; transform: translateX(-60px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes side2In { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes vsIn { from { opacity: 0; transform: scale(2); } to { opacity: 1; transform: scale(1); } }
        @keyframes rivalryPulse { 0%,100% { text-shadow: 0 0 20px currentColor; } 50% { text-shadow: 0 0 40px currentColor, 0 0 60px currentColor; } }
        .rv-main { animation: rivalryIn 0.4s ease forwards; }
        .rv-s1 { animation: side1In 0.5s ease 0.2s both; }
        .rv-s2 { animation: side2In 0.5s ease 0.2s both; }
        .rv-vs { animation: vsIn 0.4s ease 0.4s both; }
        .rv-badge { animation: rivalryIn 0.3s ease 0.1s both; }
      `}</style>
      {brandBg && <img loading="lazy" src={brandBg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(18px) brightness(0.18)', zIndex: 0 }} />}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, ' + accentColor + '18 0%, transparent 65%)', zIndex: 1 }} />

      <div className="rv-main" style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '40px 32px', maxWidth: '560px', width: '100%' }}>

        {/* Badge */}
        <div className="rv-badge" style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.52rem', letterSpacing: '4px', color: accentColor, background: accentColor + '18', border: '1px solid ' + accentColor + '55', borderRadius: '4px', padding: '4px 20px' }}>
          NEW RIVALRY
        </div>

        {/* Sides */}
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '16px', justifyContent: 'center' }}>
          {renderSide(side1, 'rv-s1')}
          <div className="rv-vs" style={{ fontFamily: "'Anton',sans-serif", fontSize: '3rem', color: accentColor, textShadow: '0 0 20px ' + accentColor + '88', flexShrink: 0, lineHeight: 1 }}>VS</div>
          {renderSide(side2, 'rv-s2')}
        </div>

        {/* Level bars */}
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginTop: '4px' }}>
          {[1,2,3,4].map(function(l) { return (
            <div key={l} style={{ width: '24px', height: '4px', borderRadius: '2px', background: l <= rivalry.level ? accentColor : 'rgba(255,255,255,0.1)', transition: 'background 0.3s' }} />
          ); })}
          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: accentColor, marginLeft: '8px', letterSpacing: '1px' }}>
            {rivalry.type === 'team' ? 'TAG RIVALRY' : 'RIVALRY'} — LVL {rivalry.level}
          </div>
        </div>

        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', marginTop: '8px' }}>
          CLICK TO CONTINUE
        </div>
      </div>
    </div>
  );
};

// ── STEP 1: Show Recap Popup ─────────────────────────────────────────
export const ShowRecapPopup = ({ gameData, brandColor, brandBg, onClose, onStart }) => {
  const slots = (gameData && gameData.bookingSlots) || [];
  const week = (gameData && gameData.week) || 1;
  const isPLEWeek = !!(gameData && gameData.week && gameData.week % 5 === 0);
  // Read from pleSlotsV2 (new) with fallback to pleBooking (legacy)
  const pleSlotsV2RC = (gameData && gameData.pleSlotsV2) || [];
  const openerSlotRC = pleSlotsV2RC.find(function(s) { return s.type === 'opener'; });
  const mainSlotRC   = pleSlotsV2RC.find(function(s) { return s.type === 'main_event'; });
  const openerParts = (openerSlotRC && openerSlotRC.booking && openerSlotRC.booking.participants) ||
                      ((gameData && gameData.pleBooking && gameData.pleBooking.openerParticipants) || []);
  const mainParts   = (mainSlotRC && mainSlotRC.booking && mainSlotRC.booking.participants) ||
                      ((gameData && gameData.pleBooking && gameData.pleBooking.mainEventParticipants) || []);
  const rivals = ((gameData && gameData.rivals) || []).filter(Boolean);

  var BRAND_LOGOS_RC = {
    'Raw': process.env.PUBLIC_URL + '/brands/logos/RAW25_LOGO.png',
    'SmackDown': process.env.PUBLIC_URL + '/brands/logos/SMACKDOWN25_LOGO.png',
    'NXT': process.env.PUBLIC_URL + '/brands/logos/NXT_LOGO.png',
    'NXT 2.0': process.env.PUBLIC_URL + '/brands/logos/NXT20_LOGO.png',
    'WCW': process.env.PUBLIC_URL + '/brands/logos/WCW_LOGO.png',
    'ECW': process.env.PUBLIC_URL + '/brands/logos/ECW_LOGO.png',
    'NXT Mutiny': process.env.PUBLIC_URL + '/brands/logos/NXTMUTINY_LOGO.png',
  };
  var BRAND_COLORS_RC = { 'Raw': '#c0392b', 'SmackDown': '#1a6bb5', 'NXT': '#a8a9ad', 'NXT 2.0': '#e91e8c', 'ECW': '#7b1fa2', 'WCW': '#e65100', 'NXT Mutiny': '#424242' };

  // Helper: render a single participant as a colored chip (brand or superstar)
  var renderParticipantChip = function(p, chipBrandColor, chipBrandLogo) {
    if (!p) return null;
    if (p.isBrand) {
      var bColor = p.brandColor || BRAND_COLORS_RC[p.brand_id] || '#888';
      var bLogo  = p.brandLogo  || BRAND_LOGOS_RC[p.brand_id]  || null;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: bColor + '18', border: '1px solid ' + bColor + '44', borderRadius: '3px', padding: '1px 6px' }}>
          {bLogo && <img loading="lazy" src={bLogo} alt={p.brand_id} style={{ height: '11px', objectFit: 'contain' }} onError={function(e) { e.target.style.display='none'; }} />}
          <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.4rem', color: bColor }}>{p.brand_id || p.name}</span>
        </span>
      );
    }
    // Superstar — show in my-brand encart
    var sColor = chipBrandColor || '#888';
    var sLogo  = chipBrandLogo  || null;
    var sName  = p.name || getWrestlerName(p, 'display') || '?';
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: sColor + '18', border: '1px solid ' + sColor + '44', borderRadius: '3px', padding: '1px 6px' }}>
        {sLogo && <img loading="lazy" src={sLogo} alt="" style={{ height: '11px', objectFit: 'contain' }} onError={function(e) { e.target.style.display='none'; }} />}
        <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: '#fff' }}>{sName}</span>
      </span>
    );
  };

  // PLE inter-brand row: team-aware, chips for all participants
  var renderPLERow = function(label, color, participants, matchRule) {
    if (!isPLEWeek) return null;
    var teamSizes = ALL_RULE_TEAMS[matchRule || '1v1'] || null;
    var validP = (participants || []).filter(Boolean);
    var teams;
    if (teamSizes) {
      teams = []; var off = 0;
      teamSizes.forEach(function(sz) { teams.push(validP.slice(off, off + sz)); off += sz; });
    } else {
      teams = validP.map(function(p) { return [p]; });
    }
    // My brand info for superstar chips
    var myBrandColorRC = brandColor;
    var myBrandLogoRC  = BRAND_LOGOS_RC[(gameData && gameData.brand)] || null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '6px', borderBottom: '1px solid rgba(255,255,255,0.05)', marginBottom: '4px', background: color + '08' }}>
        <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', letterSpacing: '1.5px', color, width: '90px', flexShrink: 0 }}>{label}</div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          {teams.map(function(team, ti) {
            return (
              <React.Fragment key={ti}>
                {ti > 0 && <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.5rem', color: '#e74c3c' }}>VS</span>}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                  {team.map(function(p, pi) {
                    return (
                      <React.Fragment key={pi}>
                        {pi > 0 && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: 'rgba(255,255,255,0.35)' }}>&amp;</span>}
                        {renderParticipantChip(p, myBrandColorRC, myBrandLogoRC)}
                      </React.Fragment>
                    );
                  })}
                </span>
              </React.Fragment>
            );
          })}
        </div>
        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>INTER-BRAND</div>
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '91px', paddingBottom: '16px', background: 'rgba(0,0,0,0.92)' }}>
      <style>{css}</style>
      {brandBg && <img loading="lazy" src={brandBg} alt="" style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(18px) brightness(0.18)', zIndex: 0 }} />}
      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '680px', margin: '0 16px', ...S.panel, overflow: 'hidden', maxHeight: 'calc(100vh - 107px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, ' + brandColor + ', transparent)' }} />
        {/* Header */}
        <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ ...S.anton, fontSize: '1.4rem', letterSpacing: '4px', color: '#fff' }}>SHOW CARD</div>
            <div style={{ ...S.mono, fontSize: '0.5rem', color: brandColor, letterSpacing: '2px', marginTop: '2px' }}>WEEK {week} — READ ONLY</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
        </div>
        {/* Slots list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 24px' }}>
          {/* PLE Opener */}
          {isPLEWeek && renderPLERow('OPENER', '#2ecc71', openerParts, openerSlotRC && openerSlotRC.booking && openerSlotRC.booking.matchRule)}
          {slots.map((slot, i) => {
            // For PLE weeks: opener and main_event are shown via renderPLERow — skip here
            if (isPLEWeek && (slot.type === 'opener' || slot.type === 'main_event' || slot.isInterBrand)) return null;
            const isPromo = slot.type === 'promo';
            const p = slot.booking && slot.booking.participants || [];
            const promoActionData = isPromo && slot.booking && slot.booking.promoAction ? PROMO_ACTIONS.find(a => a.id === slot.booking.promoAction) : null;
            const matchTypeData = !isPromo && slot.booking && slot.booking.matchType ? MATCH_TYPES.find(m => m.id === slot.booking.matchType) : null;
            const matchRuleData = !isPromo && slot.booking && slot.booking.matchRule ? MATCH_RULES.find(r => r.id === slot.booking.matchRule) : null;
            const slotColor = isPromo ? '#9b59b6' : slot.type === 'main_event' ? brandColor : 'rgba(255,255,255,0.6)';

            return (
              <div key={i} className="sr-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '6px', borderBottom: '1px solid rgba(255,255,255,0.05)', marginBottom: '4px', background: 'transparent', transition: 'background 0.15s' }}>
                <div style={{ ...S.anton, fontSize: '0.6rem', letterSpacing: '1.5px', color: slotColor, width: '90px', flexShrink: 0 }}>{SLOT_LABELS[slot.type] || slot.type}</div>
                <div style={{ flex: 1 }}>
                  {slot.booking ? (
                    isPromo ? (
                      <div>
                        <div style={{ ...S.mono, fontSize: '0.55rem', color: '#fff' }}>{promoActionData ? promoActionData.name : 'Promo'}</div>
                        <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.45)', marginTop: '2px' }}>
                          {p.map(w => w && getWrestlerName(w, 'display')).filter(Boolean).join(' & ')}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ ...S.mono, fontSize: '0.55rem', color: '#fff' }}>
                          {renderMatchParticipants(p, slot.booking && slot.booking.matchRule, null, S)}
                        </div>
                        <div style={{ display: 'flex', gap: '6px', marginTop: '3px' }}>
                          {matchRuleData && matchRuleData.id !== '1v1' && <span style={{ ...S.mono, fontSize: '0.38rem', color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.06)', padding: '1px 5px', borderRadius: '2px' }}>{matchRuleData.name}</span>}
                          {matchTypeData && matchTypeData.id !== 'normal' && <span style={{ ...S.mono, fontSize: '0.38rem', color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.06)', padding: '1px 5px', borderRadius: '2px' }}>{matchTypeData.name}</span>}
                          {slot.booking.titleMatch && <span style={{ ...S.mono, fontSize: '0.38rem', color: '#d4af37', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)', padding: '1px 6px', borderRadius: '2px' }}>🏆 TITLE</span>}
                        </div>
                      </div>
                    )
                  ) : (
                    <div style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.2)' }}>— Not booked —</div>
                  )}
                </div>
                <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.25)' }}>${(slot.booking && slot.booking.cost || 0).toLocaleString('en-US')}</div>
              </div>
            );
          })}
          {/* PLE Main Event */}
          {isPLEWeek && renderPLERow('MAIN EVENT', brandColor, mainParts, mainSlotRC && mainSlotRC.booking && mainSlotRC.booking.matchRule)}
        </div>
        {/* Stats bar — fans / budget / show cost */}
        {(() => {
          var fans = (gameData && gameData.fans) || 0;
          var budget = (gameData && gameData.budget) || 0;
          var showCost = slots.reduce(function(t, s) { return t + ((s.booking && s.booking.cost) || 0); }, 0);
          var overBudget = showCost > budget;
          return (
            <div style={{ padding: '10px 24px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', gap: '0', background: 'rgba(0,0,0,0.3)' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', borderRight: '1px solid rgba(255,255,255,0.06)', paddingRight: '12px' }}>
                <div style={{ ...S.mono, fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1.5px' }}>FANS</div>
                <div style={{ ...S.anton, fontSize: '0.78rem', color: 'rgba(255,255,255,0.85)', letterSpacing: '1px' }}>{fans.toLocaleString('fr-FR')}</div>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', borderRight: '1px solid rgba(255,255,255,0.06)', padding: '0 12px' }}>
                <div style={{ ...S.mono, fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1.5px' }}>BUDGET</div>
                <div style={{ ...S.anton, fontSize: '0.78rem', color: overBudget ? '#e74c3c' : '#d4af37', letterSpacing: '1px' }}>${(budget / 1000000).toFixed(2)}M</div>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', paddingLeft: '12px' }}>
                <div style={{ ...S.mono, fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1.5px' }}>SHOW COST</div>
                <div style={{ ...S.anton, fontSize: '0.78rem', color: overBudget ? '#e74c3c' : 'rgba(255,255,255,0.7)', letterSpacing: '1px' }}>${(showCost / 1000).toFixed(1)}K</div>
              </div>
            </div>
          );
        })()}
        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
          <button className="sr-btn" onClick={onClose} style={{ ...S.mono, fontSize: '0.5rem', letterSpacing: '1px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.5)', padding: '10px 22px', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>← BACK</button>
          <button className="sr-btn" onClick={onStart} style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '2px', background: brandColor + '22', border: '1px solid ' + brandColor, color: brandColor, padding: '10px 32px', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>▶ START RESULTS</button>
        </div>
      </div>
    </div>
  );
};

// ── STEP 2: Match Result Modal ───────────────────────────────────────
const RIVALRY_IMPACT_OPTIONS = [
  { id: 'create', label: '⚡ CREATE RIVALRY', color: '#f1c40f' },
  { id: 'heatup', label: '🔥 HEAT UP', color: '#e74c3c' },
  { id: 'maintain', label: '— MAINTAIN', color: 'rgba(255,255,255,0.5)' },
  { id: 'cooldown', label: '❄ COOL DOWN', color: '#3498db' },
  { id: 'end', label: '✓ END', color: '#888' },
];

export const MatchResultModal = ({ slot, gameData, brandColor, brandBg, onSave, onSkip, onBack, slotIndex, onCancel }) => {
  const isPromo = slot.type === 'promo';
  const isCallOut = isPromo && slot.booking && (slot.booking.promoAction === 'call-out' || slot.booking.promoAction === 'tag-team-call-out');
  const callers = (slot.booking && slot.booking.participants) || [];
  const targets = (isCallOut && slot.booking && slot.booking.promoTargets) || [];
  const guestReferee = (!isPromo && slot.booking && slot.booking.guestReferee) || null;
  const p = isCallOut ? [...callers, ...targets].filter(Boolean) : callers;
  // For rivalry selection, include guest referee as selectable
  const pForRivalry = guestReferee ? [...p, guestReferee].filter(Boolean) : p;
  const promoActionData = isPromo ? PROMO_ACTIONS.find(a => a.id === (slot.booking && slot.booking.promoAction)) : null;
  const matchTypeData = !isPromo && MATCH_TYPES.find(m => m.id === (slot.booking && slot.booking.matchType));
  const isSpecialGuestRefSR = !isPromo && slot.booking && slot.booking.matchType === 'special-guest-referee';
  const slotColor = isPromo ? '#9b59b6' : slot.type === 'main_event' ? brandColor : 'rgba(255,255,255,0.8)';

  // Title match info
  const championsRaw = (gameData && gameData.champions) || {};
  const champSlots = championsRaw.slots || [];
  const champMap = championsRaw.champions || {};
  const titleSlot = slot.booking && slot.booking.titleMatch ? champSlots.find(s => s.key === slot.booking.titleMatch) : null;
  const titleCurrentChamps = titleSlot ? (champMap[titleSlot.key] || []) : [];
  const titleRender = titleSlot ? (titleSlot.titleOverride && (titleSlot.titleOverride.render || getTitleRender(titleSlot.titleOverride.id))) : null;
  const titleName = titleSlot ? (titleSlot.titleOverride && titleSlot.titleOverride.name || titleSlot.key) : '';

  // Determine if winner is contender (not current champ)
  const isChampion = (w) => titleCurrentChamps.some(c => c && w && c.wrestler_id === w.wrestler_id);

  const [winner, setWinner] = useState(null); // wrestler obj or 'nc'
  const [noContest, setNoContest] = useState(false);
  const [titleChange, setTitleChange] = useState(false);
  const [rating, setRating] = useState(0);
  const [rivalryImpact, setRivalryImpact] = useState(null); // kept for save compat — driven by rivalryActions
  const [rivalryActions, setRivalryActions] = useState({}); // { rivalryId: 'heatup'|'cooldown'|'maintain'|'end' }
  const [newRivalryActions, setNewRivalryActions] = useState([]); // array of { side1Ids, side2Ids, type } for creates

  // Find ALL rivalries involving participants
  var rivalries = (gameData && gameData.rivalries) || [];
  var pIds = p.map(function(x) { return x && x.wrestler_id; }).filter(Boolean);
  var involvedRivalries = rivalries.filter(function(rv) {
    var side1Ids = (rv.side1 || []).map(String);
    var side2Ids = (rv.side2 || []).map(String);
    var allRvIds = side1Ids.concat(side2Ids);
    // ALL members of both sides must be present in the match participants
    return allRvIds.length > 0 && allRvIds.every(function(rid) {
      return pIds.some(function(id) { return String(id) === rid; });
    });
  });
  var LEVEL_LABELS_RM = { 1: 'Lvl.1 — Beginning', 2: 'Lvl.2 — Intense', 3: 'Lvl.3 — Heated', 4: 'Lvl.4 — Blood Feud' };
  var LEVEL_COLORS_RM = { 1: '#3498db', 2: '#e67e22', 3: '#e74c3c', 4: '#d4af37' };
  // Keep existingRivalry for backward compat
  var existingRivalry = involvedRivalries[0] || null;
  const [newRivalryType, setNewRivalryType] = useState('1v1');
  const [rivalrySide1, setRivalrySide1] = useState([]);
  const [rivalrySide2, setRivalrySide2] = useState([]);
  // Multi-create: array of { type, side1, side2 }
  const [pendingCreates, setPendingCreates] = useState([]);
  const [pendingStorylineId, setPendingStorylineId] = useState(null);
  const [pendingStorylineTitle, setPendingStorylineTitle] = useState(null); // titleSlotKey for requiresTitle storylines
  const [showStorylinePicker, setShowStorylinePicker] = useState(false);
  const [injuries, setInjuries] = useState([]); // [{ wrestler, weeks }]
  const [notes, setNotes] = useState('');

  // Auto detect title change
  const handleSetWinner = (w) => {
    setWinner(w);
    if (titleSlot && w && w !== 'nc') {
      setTitleChange(!isChampion(w));
    } else {
      setTitleChange(false);
    }
  };

  const canSave = isPromo ? true : (noContest || winner !== null);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', overflowY: 'auto', paddingTop: '91px' }}>
      {/* Full-screen background — same as show recap/animations */}
      <div style={{ position: 'fixed', inset: 0, background: '#0a0a0a', zIndex: 0 }} />
      {brandBg && <img loading="lazy" src={brandBg} alt="" style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(18px) brightness(0.18)', zIndex: 1 }} />}
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2 }} />
      <style>{css}</style>
      <div style={{ width: '100%', maxWidth: '680px', margin: '16px', ...S.panel, overflow: 'hidden', maxHeight: 'calc(100vh - 107px)', display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 3 }}>
        <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, ' + slotColor + ', transparent)' }} />

        {/* Header */}
        <div style={{ padding: '16px 22px 12px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ ...S.anton, fontSize: '0.65rem', letterSpacing: '2px', color: slotColor }}>{SLOT_LABELS[slot.type]}</div>
            <div style={{ ...S.mono, fontSize: '0.52rem', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
              {isPromo ? (promoActionData && promoActionData.name) : (matchTypeData && matchTypeData.id !== 'normal' ? matchTypeData.name : 'Standard Match')}
            </div>
          </div>
          {titleSlot && titleRender && (
            <img loading="lazy" src={titleRender} alt={titleName} style={{ height: '36px', objectFit: 'contain', filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.6))' }} onError={e => { e.target.style.display='none'; }} />
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: '18px' }}>

          {/* Promo participants display */}
          {isPromo && p.length > 0 && (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '10px 0' }}>
              {callers.filter(Boolean).map((w, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                  <img loading="lazy" src={getWrestlerRender(w) || placeholder} alt={w.name} style={{ width: '44px', height: '58px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.8))' }} />
                  <div style={{ ...S.mono, fontSize: '0.38rem', color: '#fff' }}>{getWrestlerNameUpper(w, 'display')}</div>
                </div>
              ))}
              {isCallOut && targets.filter(Boolean).length > 0 && (
                <>
                  <div style={{ ...S.anton, fontSize: '0.8rem', color: '#e74c3c' }}>→</div>
                  {targets.filter(Boolean).map((w, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      <img loading="lazy" src={getWrestlerRender(w) || placeholder} alt={w.name} style={{ width: '44px', height: '58px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.8))' }} />
                      <div style={{ ...S.mono, fontSize: '0.38rem', color: '#fff' }}>{getWrestlerNameUpper(w, 'display')}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Participants / Winner selection - matches only */}
          {!isPromo && (
            <div>
              <div style={{ ...S.label, marginBottom: '10px' }}>SELECT WINNER</div>
              {(() => {
                var matchRule = slot.booking && slot.booking.matchRule;
                var RULE_TEAMS_SR = {
                  'tag': [2,2], '3v3': [3,3], '4v4': [4,4],
                  'tag-triple-threat': [2,2,2], 'tag-fatal-4-way': [2,2,2,2],
                  '1v2': [1,2], '1v3': [1,3], '2v3': [2,3],
                  '2v2': [2,2],
                };
                var teamSizesSR = RULE_TEAMS_SR[matchRule];
                var isTeamMatch = !!teamSizesSR && teamSizesSR.some(function(s) { return s > 1; });
                var validP = p.filter(Boolean);

                if (isTeamMatch && teamSizesSR && validP.length > 0) {
                  // Split into teams using exact team sizes from rule
                  var teams = [];
                  var offset = 0;
                  teamSizesSR.forEach(function(sz) {
                    teams.push(validP.slice(offset, offset + sz));
                    offset += sz;
                  });
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {teams.map((team, ti) => {
                        var teamWon = winner && !noContest && team.some(function(w) { return winner.wrestler_id === w.wrestler_id; });
                        return (
                          <div key={ti} onClick={() => { handleSetWinner(team[0]); }}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '8px', border: '1px solid ' + (teamWon ? brandColor : 'rgba(255,255,255,0.1)'), background: teamWon ? brandColor + '15' : 'rgba(255,255,255,0.02)', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s' }}>
                            {teamWon && <span style={{ color: brandColor }}>✓</span>}
                            {team.map(function(w, wi) { return (
                              <div key={wi} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <img loading="lazy" src={getWrestlerRender(w) || placeholder} alt="" style={{ width: '28px', height: '36px', objectFit: 'contain', objectPosition: 'top' }} />
                                <div style={{ ...S.anton, fontSize: '0.55rem', color: teamWon ? brandColor : '#fff' }}>{getWrestlerNameUpper(w, 'display')}</div>
                                {isChampion(w) && <span style={{ ...S.mono, fontSize: '0.36rem', color: '#d4af37' }}>👑</span>}
                              </div>
                            ); })}
                          </div>
                        );
                      })}
                      <div onClick={() => { setNoContest(true); setWinner('nc'); setTitleChange(false); }}
                        style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid ' + (noContest ? 'rgba(231,76,60,0.6)' : 'rgba(255,255,255,0.1)'), background: noContest ? 'rgba(231,76,60,0.1)' : 'transparent', cursor: 'pointer' }}>
                        <div style={{ ...S.mono, fontSize: '0.52rem', color: noContest ? '#e74c3c' : 'rgba(255,255,255,0.4)' }}>NO CONTEST / DQ</div>
                      </div>
                    </div>
                  );
                }

                // Standard 1v1 / multi-person
                return (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {validP.map((w, i) => {
                      const isWinner = winner && winner.wrestler_id === w.wrestler_id;
                      const isChamp = isChampion(w);
                      return (
                        <div key={i} onClick={() => handleSetWinner(w)}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '8px', border: '1px solid ' + (isWinner ? brandColor : 'rgba(255,255,255,0.12)'), background: isWinner ? brandColor + '18' : 'rgba(255,255,255,0.03)', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s', position: 'relative' }}>
                          <img loading="lazy" src={getWrestlerRender(w) || placeholder} alt="" style={{ width: '32px', height: '42px', objectFit: 'contain', objectPosition: 'top' }} />
                          <div>
                            <div style={{ ...S.anton, fontSize: '0.6rem', letterSpacing: '1px', color: isWinner ? brandColor : '#fff' }}>{getWrestlerNameUpper(w, 'display')}</div>
                            {isChamp && <div style={{ ...S.mono, fontSize: '0.38rem', color: '#d4af37' }}>👑 CHAMPION</div>}
                          </div>
                          {isWinner && <div style={{ position: 'absolute', top: '4px', right: '6px', color: brandColor }}>✓</div>}
                        </div>
                      );
                    })}
                    <div onClick={() => { setNoContest(true); setWinner('nc'); setTitleChange(false); }}
                      style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid ' + (noContest ? 'rgba(231,76,60,0.6)' : 'rgba(255,255,255,0.1)'), background: noContest ? 'rgba(231,76,60,0.1)' : 'transparent', cursor: 'pointer' }}>
                      <div style={{ ...S.mono, fontSize: '0.52rem', color: noContest ? '#e74c3c' : 'rgba(255,255,255,0.4)' }}>NO CONTEST / DQ</div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Title change detection */}
          {titleSlot && winner && winner !== 'nc' && (
            <div onClick={() => setTitleChange(t => !t)}
              style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px', borderRadius: '8px', border: '1px solid ' + (titleChange ? 'rgba(212,175,55,0.6)' : 'rgba(255,255,255,0.1)'), background: titleChange ? 'rgba(212,175,55,0.08)' : 'rgba(255,255,255,0.02)', cursor: 'pointer', transition: 'background 0.18s, border-color 0.18s' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '4px', border: '1px solid ' + (titleChange ? '#d4af37' : 'rgba(255,255,255,0.25)'), background: titleChange ? '#d4af37' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {titleChange && <span style={{ color: '#000', fontSize: '0.6rem', fontWeight: 'bold' }}>✓</span>}
              </div>
              {titleRender && <img loading="lazy" src={titleRender} alt="" style={{ height: '28px', objectFit: 'contain', opacity: titleChange ? 1 : 0.4 }} onError={e => { e.target.style.display='none'; }} />}
              <div>
                <div style={{ ...S.mono, fontSize: '0.52rem', color: titleChange ? '#d4af37' : 'rgba(255,255,255,0.5)' }}>TITLE CHANGE</div>
                <div style={{ ...S.mono, fontSize: '0.42rem', color: titleChange ? '#d4af37' : 'rgba(255,255,255,0.3)', marginTop: '2px' }}>
                  {titleChange ? '👑 ' + (winner && winner.name && winner.name.toUpperCase()) + ' IS THE NEW CHAMPION' : titleName.toUpperCase() + ' — NO CHANGE'}
                </div>
              </div>
            </div>
          )}

          {/* Rivalry impact — only for matches and call-outs */}
          {(isCallOut || (!isPromo)) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ ...S.label }}>RIVALRY IMPACT</div>

              {/* One block per involved rivalry */}
              {involvedRivalries.map(function(rv) {
                var rvId = rv.id;
                var curLevel = rv.level || 1;
                var action = rivalryActions[rvId] || null;
                var newLevel = action === 'heatup' ? Math.min(4, curLevel + 1) : action === 'heatup2' ? Math.min(4, curLevel + 2) : action === 'cooldown' ? Math.max(1, curLevel - 1) : curLevel;
                var rvColor = LEVEL_COLORS_RM[curLevel] || '#fff';
                var newColor = LEVEL_COLORS_RM[newLevel] || '#fff';
                // Names for this rivalry
                var getRvName = function(ids) { return ids.map(function(id) { return getWrestlerName(p.find(function(x) { return x && String(x.wrestler_id) === String(id); }) || null, 'display') || '?'; }).filter(function(n) { return n !== '?'; }).join(' & '); };
                var s1Name = getRvName(rv.side1 || []);
                var s2Name = getRvName(rv.side2 || []);
                var rvLabel = (s1Name || '?') + ' vs ' + (s2Name || '?');
                return (
                  <div key={rvId} style={{ padding: '10px 12px', background: rvColor + '08', border: '1px solid ' + rvColor + '33', borderRadius: '8px' }}>
                    {/* Rivalry header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', gap: '2px' }}>
                        {[1,2,3,4].map(function(l) { return <div key={l} style={{ width: '12px', height: '3px', borderRadius: '1px', background: l <= curLevel ? rvColor : 'rgba(255,255,255,0.1)' }} />; })}
                      </div>
                      <div style={{ ...S.mono, fontSize: '0.42rem', color: rvColor, letterSpacing: '0.5px' }}>{rvLabel.toUpperCase()}</div>
                    </div>
                    {/* Impact buttons */}
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      {[
                        { id: 'heatup', label: '🔥 HEAT UP', color: '#e74c3c' },
                        { id: 'heatup2', label: '🔥🔥 +2 LVL', color: '#c0392b' },
                        { id: 'maintain', label: '— MAINTAIN', color: 'rgba(255,255,255,0.5)' },
                        { id: 'cooldown', label: '❄ COOL DOWN', color: '#3498db' },
                        { id: 'end', label: '✓ END', color: '#888' },
                      ].filter(function(opt) {
                        // Hide +2 LVL if already at lvl 3 or 4 (can't go above 4)
                        if (opt.id === 'heatup2' && curLevel >= 3) return false;
                        return true;
                      }).map(function(opt) {
                        var isSelected = action === opt.id;
                        return (
                          <button key={opt.id} onClick={function() {
                            var updated = { ...rivalryActions };
                            if (isSelected) delete updated[rvId]; else updated[rvId] = opt.id;
                            setRivalryActions(updated);
                            // Keep single rivalryImpact in sync with first rivalry for save compat
                            var firstRvId = involvedRivalries[0] && involvedRivalries[0].id;
                            if (rvId === firstRvId) setRivalryImpact(isSelected ? null : opt.id);
                          }}
                            style={{ ...S.mono, fontSize: '0.42rem', letterSpacing: '0.5px', background: isSelected ? opt.color + '22' : 'transparent', border: '1px solid ' + (isSelected ? opt.color : 'rgba(255,255,255,0.12)'), color: isSelected ? opt.color : 'rgba(255,255,255,0.4)', padding: '5px 12px', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    {/* Level preview when selected */}
                    {(action === 'heatup' || action === 'heatup2' || action === 'cooldown' || action === 'maintain') && (
                      <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ display: 'flex', gap: '3px' }}>
                          {[1,2,3,4].map(function(l) { return <div key={l} style={{ width: '14px', height: '3px', borderRadius: '1px', background: l <= newLevel ? newColor : 'rgba(255,255,255,0.1)', transition: 'background 0.3s' }} />; })}
                        </div>
                        {(action === 'heatup' || action === 'heatup2' || action === 'cooldown')
                          ? <div style={{ ...S.mono, fontSize: '0.4rem', color: 'rgba(255,255,255,0.4)' }}>Lvl.{curLevel} → <span style={{ color: newColor }}>Lvl.{newLevel}</span></div>
                          : null}
                        <div style={{ ...S.mono, fontSize: '0.42rem', color: newColor }}>{LEVEL_LABELS_RM[newLevel]}</div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* If no rivalry involved — show single CREATE option */}
              {involvedRivalries.length === 0 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {[{ id: 'create', label: '⚡ CREATE RIVALRY', color: '#f1c40f' }].map(function(opt) {
                    var isSelected = rivalryImpact === opt.id;
                    return (
                      <button key={opt.id} onClick={function() { setRivalryImpact(isSelected ? null : opt.id); }}
                        style={{ ...S.mono, fontSize: '0.46rem', letterSpacing: '0.5px', background: isSelected ? opt.color + '22' : 'transparent', border: '1px solid ' + (isSelected ? opt.color : 'rgba(255,255,255,0.12)'), color: isSelected ? opt.color : 'rgba(255,255,255,0.4)', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Pending creates list */}
              {pendingCreates.map(function(pc, pci) {
                return (
                  <div key={pci} style={{ padding: '8px 12px', background: 'rgba(241,196,15,0.06)', border: '1px solid rgba(241,196,15,0.3)', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ ...S.mono, fontSize: '0.42rem', color: '#f1c40f' }}>⚡ {pc.side1.map(function(w) { return w.name; }).join(' & ')} VS {pc.side2.map(function(w) { return w.name; }).join(' & ')}</div>
                    <div style={{ ...S.mono, fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', padding: '1px 5px' }}>{pc.type.toUpperCase()}</div>
                    <button onClick={function() { setPendingCreates(pendingCreates.filter(function(_, i) { return i !== pci; })); }}
                      style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid rgba(231,76,60,0.3)', color: '#e74c3c', borderRadius: '3px', padding: '1px 6px', cursor: 'pointer', fontFamily: "'Monda',sans-serif", fontSize: '0.38rem' }}>✕</button>
                  </div>
                );
              })}

              {/* CREATE button — always available */}
              <div>
                <button onClick={function() { setRivalryImpact(rivalryImpact === 'create' ? null : 'create'); setRivalrySide1([]); setRivalrySide2([]); setNewRivalryType('1v1'); }}
                  style={{ ...S.mono, fontSize: '0.42rem', letterSpacing: '0.5px', background: rivalryImpact === 'create' ? 'rgba(241,196,15,0.15)' : 'transparent', border: '1px solid ' + (rivalryImpact === 'create' ? 'rgba(241,196,15,0.5)' : 'rgba(255,255,255,0.1)'), color: rivalryImpact === 'create' ? '#f1c40f' : 'rgba(255,255,255,0.3)', padding: '5px 14px', borderRadius: '4px', cursor: 'pointer' }}>
                  + ⚡ CREATE NEW RIVALRY
                </button>
              </div>

              {/* New rivalry picker */}
              {rivalryImpact === 'create' && (
                <div style={{ padding: '12px 14px', background: 'rgba(241,196,15,0.07)', border: '1px solid rgba(241,196,15,0.25)', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ ...S.label, color: '#f1c40f' }}>⚡ NEW RIVALRY — LEVEL 1</div>
                    {/* Confirm this create */}
                    <button onClick={function() {
                      if (rivalrySide1.length === 0 || rivalrySide2.length === 0) return;
                      setPendingStorylineId(null);
                      setShowStorylinePicker(true);
                    }}
                      disabled={rivalrySide1.length === 0 || rivalrySide2.length === 0}
                      style={{ ...S.mono, fontSize: '0.42rem', background: (rivalrySide1.length > 0 && rivalrySide2.length > 0) ? 'rgba(241,196,15,0.15)' : 'transparent', border: '1px solid ' + ((rivalrySide1.length > 0 && rivalrySide2.length > 0) ? 'rgba(241,196,15,0.6)' : 'rgba(255,255,255,0.1)'), color: (rivalrySide1.length > 0 && rivalrySide2.length > 0) ? '#f1c40f' : 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '4px', cursor: (rivalrySide1.length > 0 && rivalrySide2.length > 0) ? 'pointer' : 'not-allowed' }}>
                      ✓ NEXT →
                    </button>
                  </div>
                  {/* Type selector */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {['1v1', '2v2'].map(t => (
                      <button key={t} onClick={() => { setNewRivalryType(t); setRivalrySide1([]); setRivalrySide2([]); }}
                        style={{ ...S.mono, fontSize: '0.5rem', letterSpacing: '1px', background: newRivalryType === t ? 'rgba(241,196,15,0.15)' : 'transparent', border: '1px solid ' + (newRivalryType === t ? 'rgba(241,196,15,0.6)' : 'rgba(255,255,255,0.15)'), color: newRivalryType === t ? '#f1c40f' : 'rgba(255,255,255,0.4)', padding: '5px 16px', borderRadius: '4px', cursor: 'pointer' }}>
                        {t.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  {/* Side selectors */}
                  {(() => {
                    var intA = slot && slot.booking && slot.booking.interferenceA;
                    var allPool = [...pForRivalry.filter(Boolean)];
                    if (intA && !allPool.some(function(x) { return x.wrestler_id === intA.wrestler_id; })) allPool = [...allPool, intA];
                    var renderSide = function(sideNum) {
                      var isSide1 = sideNum === 1;
                      var sideList = isSide1 ? rivalrySide1 : rivalrySide2;
                      var otherList = isSide1 ? rivalrySide2 : rivalrySide1;
                      var sideColor = isSide1 ? '#f1c40f' : '#e74c3c';
                      var setSide = isSide1 ? setRivalrySide1 : setRivalrySide2;
                      var maxReached = sideList.length >= (newRivalryType === '2v2' ? 2 : 1);
                      return (
                        <div>
                          <div style={{ ...S.label, marginBottom: '5px' }}>SIDE {sideNum}</div>
                          {allPool.map(function(w, i) {
                            var isSel = sideList.some(function(s) { return s.wrestler_id === w.wrestler_id; });
                            var isOther = otherList.some(function(s) { return s.wrestler_id === w.wrestler_id; });
                            var isInterf = intA && w.wrestler_id === intA.wrestler_id;
                            return (
                              <div key={i} onClick={function() {
                                if (isOther) return;
                                if (isSel) setSide(sideList.filter(function(s) { return s.wrestler_id !== w.wrestler_id; }));
                                else if (!maxReached) setSide([...sideList, w]);
                              }}
                              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 7px', borderRadius: '4px', border: '1px solid ' + (isSel ? sideColor : isInterf ? 'rgba(231,76,60,0.3)' : 'rgba(255,255,255,0.08)'), background: isSel ? sideColor + '18' : isInterf ? 'rgba(231,76,60,0.05)' : 'transparent', cursor: isOther ? 'not-allowed' : 'pointer', opacity: isOther ? 0.3 : 1, marginBottom: '3px' }}>
                                <img loading="lazy" src={getWrestlerRender(w) || placeholder} alt="" style={{ width: '18px', height: '24px', objectFit: 'contain', objectPosition: 'top' }} />
                                <div style={{ ...S.mono, fontSize: '0.44rem', color: isSel ? sideColor : '#fff' }}>{getWrestlerName(w, 'display')}</div>
                                {isInterf && !isSel && <span style={{ ...S.mono, fontSize: '0.36rem', color: 'rgba(231,76,60,0.7)', marginLeft: 'auto' }}>⚡ INTERF.</span>}
                                {isSel && <span style={{ marginLeft: 'auto', color: sideColor, fontSize: '0.5rem' }}>✓</span>}
                              </div>
                            );
                          })}
                        </div>
                      );
                    };
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '8px', alignItems: 'center' }}>
                        {renderSide(1)}
                        <div style={{ ...S.anton, fontSize: '0.8rem', color: '#e74c3c', textAlign: 'center' }}>VS</div>
                        {renderSide(2)}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Rating — only for matches */}
          {!isPromo && (
            <div>
              <div style={{ ...S.label, marginBottom: '10px' }}>MATCH RATING</div>
              <StarRating value={rating} onChange={setRating} size={28} />
            </div>
          )}


          {/* Injuries */}
          {!isPromo && p.length > 0 && (
            <div>
              <div style={{ ...S.label, marginBottom: '8px' }}>🤕 INJURIES</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {p.filter(Boolean).map((w, i) => {
                  var inj = injuries.find(function(x) { return x.wrestler.wrestler_id === w.wrestler_id; });
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '6px', border: '1px solid ' + (inj ? 'rgba(231,76,60,0.4)' : 'rgba(255,255,255,0.07)'), background: inj ? 'rgba(231,76,60,0.06)' : 'transparent' }}>
                      <img loading="lazy" src={process.env.PUBLIC_URL + '/icons/injury.png'} alt="" style={{ width: '18px', height: '18px', objectFit: 'contain', opacity: inj ? 1 : 0.2 }} onError={e => { e.target.style.display='none'; }} />
                      <img loading="lazy" src={getWrestlerRender(w) || placeholder} alt="" style={{ width: '28px', height: '36px', objectFit: 'contain', objectPosition: 'top' }} />
                      <div style={{ ...S.mono, fontSize: '0.5rem', color: inj ? '#e74c3c' : 'rgba(255,255,255,0.6)', flex: 1 }}>{getWrestlerNameUpper(w, 'display')}</div>
                      {inj ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <input type="number" min="1" max="52" value={inj.weeks}
                            onChange={e => setInjuries(injuries.map(function(x) { return x.wrestler.wrestler_id === w.wrestler_id ? { ...x, weeks: Number(e.target.value) || 1 } : x; }))}
                            style={{ width: '48px', background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.4)', borderRadius: '4px', padding: '3px 6px', color: '#e74c3c', fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', outline: 'none', textAlign: 'center' }} />
                          <div style={{ ...S.mono, fontSize: '0.44rem', color: 'rgba(255,255,255,0.4)' }}>weeks</div>
                          <button onClick={() => setInjuries(injuries.filter(function(x) { return x.wrestler.wrestler_id !== w.wrestler_id; }))}
                            style={{ background: 'transparent', border: 'none', color: 'rgba(231,76,60,0.6)', cursor: 'pointer', fontSize: '0.7rem', padding: '0 4px' }}>✕</button>
                        </div>
                      ) : (
                        <button onClick={() => setInjuries([...injuries, { wrestler: w, weeks: 4 }])}
                          style={{ ...S.mono, fontSize: '0.4rem', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.3)', color: 'rgba(231,76,60,0.7)', padding: '3px 10px', borderRadius: '3px', cursor: 'pointer' }}>
                          + INJURED
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Notes */}
          <div>
            <div style={{ ...S.label, marginBottom: '6px' }}>NOTES</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '8px 12px', color: '#fff', fontSize: '0.55rem', fontFamily: "'Monda',sans-serif", outline: 'none', resize: 'vertical', minHeight: '70px', lineHeight: '1.5' }}
              placeholder="Notes on this segment..." />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 22px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            {slotIndex > 0 && onBack && (
              <button className="sr-btn" onClick={onBack} style={{ ...S.mono, fontSize: '0.46rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>← BACK</button>
            )}
            <button className="sr-btn" onClick={onSkip} style={{ ...S.mono, fontSize: '0.46rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.35)', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>SKIP →</button>
          </div>
          <button className="sr-btn" onClick={() => canSave && onSave({ winner, noContest, titleChange, titleSlotKey: (titleSlot && titleSlot.key), newChampion: titleChange ? winner : null, rating, rivalryImpact, rivalryActions, pendingCreates, newRivalryType: rivalryImpact === 'create' ? newRivalryType : null, rivalrySide1: rivalryImpact === 'create' ? rivalrySide1 : [], rivalrySide2: rivalryImpact === 'create' ? rivalrySide2 : [], participants: p, callers, targets, notes, injuries, matchRule: (slot.booking && slot.booking.matchRule) || null })}
            disabled={!canSave}
            style={{ ...S.mono, fontSize: '0.52rem', letterSpacing: '1.5px', background: canSave ? brandColor + '22' : 'transparent', border: '1px solid ' + (canSave ? brandColor : 'rgba(255,255,255,0.12)'), color: canSave ? brandColor : 'rgba(255,255,255,0.2)', padding: '8px 28px', borderRadius: '4px', cursor: canSave ? 'pointer' : 'not-allowed', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>
            CONFIRM →
          </button>
        </div>
      </div>
      {/* ── STORYLINE PICKER MODAL ── */}
      {showStorylinePicker && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 950, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '91px', paddingBottom: '16px' }}>
          <div style={{ width: '580px', maxHeight: 'calc(100vh - 123px)', display: 'flex', flexDirection: 'column', background: 'rgba(8,6,4,0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '3px' }}>NEW RIVALRY · OPTIONAL</div>
              <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1rem', letterSpacing: '2px', color: '#fff' }}>CHOOSE A STORYLINE</div>
            </div>
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
              <div style={{ padding: '10px 18px', fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', letterSpacing: '1.5px', color: '#f1c40f', borderBottom: '2px solid #f1c40f' }}>
                {newRivalryType === '2v2' ? '🤝 TAG TEAM' : '⚔️ SOLO'}
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {STORYLINES.filter(function(s) {
                return newRivalryType === '2v2' ? s.category === 'tag' : s.category === 'solo';
              }).map(function(s) {
                var isSelected = pendingStorylineId === s.id;
                return (
                  <div key={s.id} onClick={function() { setPendingStorylineId(isSelected ? null : s.id); if (isSelected) setPendingStorylineTitle(null); }}
                    style={{ padding: '11px 14px', background: isSelected ? 'rgba(155,89,182,0.12)' : 'rgba(255,255,255,0.03)', border: '1px solid ' + (isSelected ? 'rgba(155,89,182,0.5)' : 'rgba(255,255,255,0.07)'), borderRadius: '8px', cursor: 'pointer', position: 'relative' }}>
                    {isSelected && <div style={{ position: 'absolute', top: '8px', right: '10px', fontFamily: "'Monda',sans-serif", fontSize: '0.45rem', color: '#9b59b6' }}>✓</div>}
                    <div style={{ fontSize: '1rem', marginBottom: '5px' }}>{s.icon}</div>
                    <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', letterSpacing: '1px', color: isSelected ? '#9b59b6' : '#fff', marginBottom: '3px' }}>{s.name.toUpperCase()}</div>
                    <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.4 }}>{s.description.replace(/\{[A-Z0-9]+\}/g, '…')}</div>
                    {s.requiresTitle && <div style={{ marginTop: '5px', fontFamily: "'Monda',sans-serif", fontSize: '0.35rem', color: 'rgba(212,175,55,0.6)', letterSpacing: '1px' }}>🏆 REQUIRES TITLE</div>}
                  </div>
                );
              })}
            </div>
            {/* Title Picker — shown when selected storyline requiresTitle */}
            {pendingStorylineId && STORYLINES.find(function(s) { return s.id === pendingStorylineId && s.requiresTitle; }) && (
              <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(212,175,55,0.2)', background: 'rgba(212,175,55,0.05)', flexShrink: 0 }}>
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', letterSpacing: '1.5px', color: '#d4af37', marginBottom: '8px' }}>🏆 SELECT THE TITLE INVOLVED</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {champSlots.filter(Boolean).map(function(cs) {
                    var tName = (cs.titleOverride && cs.titleOverride.name) || cs.key;
                    var tRender = (cs.titleOverride && (cs.titleOverride.render || getTitleRender(cs.titleOverride.id)));
                    var isSelTitle = pendingStorylineTitle === cs.key;
                    return (
                      <div key={cs.key} onClick={function() { setPendingStorylineTitle(isSelTitle ? null : cs.key); }}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 10px', borderRadius: '5px', border: '1px solid ' + (isSelTitle ? 'rgba(212,175,55,0.7)' : 'rgba(255,255,255,0.12)'), background: isSelTitle ? 'rgba(212,175,55,0.12)' : 'rgba(255,255,255,0.03)', cursor: 'pointer' }}>
                        {tRender && <img loading="lazy" src={tRender} alt={tName} style={{ height: '20px', objectFit: 'contain' }} onError={function(e) { e.target.style.display='none'; }} />}
                        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: isSelTitle ? '#d4af37' : 'rgba(255,255,255,0.6)' }}>{tName}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: '8px', flexShrink: 0 }}>
              <button onClick={function() {
                setPendingCreates([...pendingCreates, { type: newRivalryType, side1: rivalrySide1, side2: rivalrySide2, storylineId: null, storylineTitleKey: null }]);
                setRivalryImpact(null); setRivalrySide1([]); setRivalrySide2([]); setNewRivalryType('1v1');
                setShowStorylinePicker(false); setPendingStorylineId(null); setPendingStorylineTitle(null);
              }} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', letterSpacing: '1px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.4)', padding: '9px 18px', borderRadius: '5px', cursor: 'pointer' }}>
                SKIP
              </button>
              <button onClick={function() {
                setPendingCreates([...pendingCreates, { type: newRivalryType, side1: rivalrySide1, side2: rivalrySide2, storylineId: pendingStorylineId, storylineTitleKey: pendingStorylineTitle }]);
                setRivalryImpact(null); setRivalrySide1([]); setRivalrySide2([]); setNewRivalryType('1v1');
                setShowStorylinePicker(false); setPendingStorylineId(null); setPendingStorylineTitle(null);
              }} style={{ flex: 1, fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', letterSpacing: '2px', background: pendingStorylineId ? 'rgba(155,89,182,0.15)' : 'rgba(241,196,15,0.1)', border: '1px solid ' + (pendingStorylineId ? 'rgba(155,89,182,0.5)' : 'rgba(241,196,15,0.4)'), color: pendingStorylineId ? '#9b59b6' : '#f1c40f', padding: '10px', borderRadius: '5px', cursor: 'pointer' }}>
                {pendingStorylineId ? '✓ CONFIRM WITH STORYLINE' : '✓ CONFIRM WITHOUT'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── STEP 3: Show Closing Screen ──────────────────────────────────────
export const ShowClosingScreen = ({ gameData, results, pleCommonResults, isPLEWeek, brandColor, brandBg, onPostShow, onReopen }) => {
  const slots = (gameData && gameData.bookingSlots) || [];
  const week = (gameData && gameData.week) || 1;
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);

  // PLE data — read from pleSlotsV2 (new) with fallback to pleBooking (legacy)
  const pleSlotsV2 = (gameData && gameData.pleSlotsV2) || [];
  const openerSlot = pleSlotsV2.find(function(s) { return s.type === 'opener'; });
  const mainSlot   = pleSlotsV2.find(function(s) { return s.type === 'main_event'; });
  const openerParts = (openerSlot && openerSlot.booking && openerSlot.booking.participants) ||
                      ((gameData && gameData.pleBooking && gameData.pleBooking.openerParticipants) || []);
  const mainParts   = (mainSlot && mainSlot.booking && mainSlot.booking.participants) ||
                      ((gameData && gameData.pleBooking && gameData.pleBooking.mainEventParticipants) || []);
  const rivals = ((gameData && gameData.rivals) || []).filter(Boolean);
  var BRAND_LOGOS_CS = {
    'Raw': process.env.PUBLIC_URL + '/brands/logos/RAW25_LOGO.png',
    'SmackDown': process.env.PUBLIC_URL + '/brands/logos/SMACKDOWN25_LOGO.png',
    'NXT': process.env.PUBLIC_URL + '/brands/logos/NXT_LOGO.png',
    'NXT 2.0': process.env.PUBLIC_URL + '/brands/logos/NXT20_LOGO.png',
    'WCW': process.env.PUBLIC_URL + '/brands/logos/WCW_LOGO.png',
    'ECW': process.env.PUBLIC_URL + '/brands/logos/ECW_LOGO.png',
    'NXT Mutiny': process.env.PUBLIC_URL + '/brands/logos/NXTMUTINY_LOGO.png',
  };
  var BRAND_COLORS_CS = { 'Raw': '#c0392b', 'SmackDown': '#1a6bb5', 'NXT': '#a8a9ad', 'NXT 2.0': '#e91e8c', 'ECW': '#7b1fa2', 'WCW': '#e65100', 'NXT Mutiny': '#424242' };

  var renderPLESegment = function(label, color, participants, pleResult, matchRule) {
    var winnerTeam = pleResult && pleResult.winnerTeam;
    var winnerBrand = pleResult && pleResult.winnerBrand;
    var myWon = pleResult && pleResult.myWon;
    // Team grouping
    var teamSizes = ALL_RULE_TEAMS[matchRule || '1v1'] || null;
    var validP = (participants || []).filter(Boolean);
    var teams;
    if (teamSizes && validP.length > 0) {
      teams = []; var tOff = 0;
      teamSizes.forEach(function(sz) { teams.push(validP.slice(tOff, tOff + sz)); tOff += sz; });
    } else {
      teams = validP.map(function(p) { return [p]; });
    }
    // Winner team members for logo display
    var winnerMembers = winnerTeam && winnerTeam.length > 0 ? winnerTeam : (winnerBrand ? [{ isBrand: true, brand_id: winnerBrand }] : []);
    return (
      <div style={{ padding: '8px 12px', borderRadius: '6px', background: color + '08', border: '1px solid ' + color + '33' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...S.mono, fontSize: '0.38rem', color, letterSpacing: '1px', marginBottom: '4px' }}>{label} — INTER-BRAND</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
              {teams.map(function(team, ti) {
                return (
                  <React.Fragment key={ti}>
                    {ti > 0 && <span style={{ color: '#e74c3c', fontSize: '0.44rem', margin: '0 2px' }}>vs</span>}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                      {team.map(function(p, pi) {
                        var isBrand = p.isBrand;
                        var pColor = isBrand ? (p.brandColor || BRAND_COLORS_CS[p.brand_id] || '#888') : brandColor;
                        var pLogo  = isBrand ? (p.brandLogo  || BRAND_LOGOS_CS[p.brand_id]  || null) : null;
                        var pName  = isBrand ? (p.brand_id || p.name) : (p.name || getWrestlerName(p, 'display') || '?');
                        return (
                          <React.Fragment key={pi}>
                            {pi > 0 && <span style={{ ...S.mono, fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)' }}>&amp;</span>}
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: pColor + '18', border: '1px solid ' + pColor + '33', borderRadius: '3px', padding: '1px 5px' }}>
                              {pLogo && <img loading="lazy" src={pLogo} alt={pName} style={{ height: '10px', objectFit: 'contain' }} onError={function(e) { e.target.style.display='none'; }} />}
                              {!pLogo && isBrand && <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.38rem', color: pColor }}>{pName}</span>}
                              {!isBrand && <span style={{ ...S.mono, fontSize: '0.44rem', color: myWon ? '#fff' : 'rgba(255,255,255,0.7)' }}>{pName}</span>}
                            </span>
                          </React.Fragment>
                        );
                      })}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          {/* Winner side — logos */}
          {winnerMembers.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {winnerMembers.map(function(p, pi) {
                  if (!p) return null;
                  var wBrandId = p.isBrand ? p.brand_id : null;
                  var wLogo = p.isBrand ? (p.brandLogo || BRAND_LOGOS_CS[wBrandId]) : null;
                  var wColor = p.isBrand ? (p.brandColor || BRAND_COLORS_CS[wBrandId] || '#888') : color;
                  var wName = p.isBrand ? (wBrandId || p.name) : (p.name || '?');
                  return (
                    <React.Fragment key={pi}>
                      {pi > 0 && <span style={{ ...S.mono, fontSize: '0.36rem', color: 'rgba(255,255,255,0.25)' }}>&amp;</span>}
                      {wLogo
                        ? <img loading="lazy" src={wLogo} alt={wName} style={{ height: '16px', objectFit: 'contain', filter: 'drop-shadow(0 0 4px ' + wColor + '88)' }} onError={function(e) { e.target.style.display='none'; }} />
                        : <span style={{ ...S.anton, fontSize: '0.46rem', color: wColor }}>{wName.toUpperCase()}</span>
                      }
                    </React.Fragment>
                  );
                })}
              </div>
              <div style={{ ...S.mono, fontSize: '0.36rem', color: myWon ? '#2ecc71' : 'rgba(255,255,255,0.35)' }}>
                {myWon ? '✓ YOUR BRAND WON' : 'WON'}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const matchResults = results.filter(r => r && !r.isPromo && r.rating > 0);
  const avgRating = matchResults.length > 0 ? (matchResults.reduce((s, r) => s + r.rating, 0) / matchResults.length).toFixed(1) : null;
  const motn = matchResults.length > 0 ? matchResults.reduce((best, r) => r.rating > (best && best.rating || 0) ? r : best, null) : null;
  const titleChanges = results.filter(r => r && r.titleChange);
  const rivalryEvents = results.filter(r => r && r.rivalryImpact && r.rivalryImpact !== 'maintain' && r.rivalryImpact !== 'none');
  const allInjuries = results.reduce(function(acc, r) { return acc.concat((r && r.injuries) || []); }, []);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '91px', paddingBottom: '16px', background: 'rgba(0,0,0,0.95)' }}>
      <style>{css}</style>
      {brandBg && <img loading="lazy" src={brandBg} alt="" style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(18px) brightness(0.18)', zIndex: 0 }} />}

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '820px', margin: '0 16px', ...S.panel, overflow: 'hidden', maxHeight: 'calc(100vh - 107px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ height: '4px', background: 'linear-gradient(90deg, transparent, ' + brandColor + ', transparent)' }} />

        {/* Header */}
        <div style={{ padding: '20px 28px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ ...S.anton, fontSize: '1.6rem', letterSpacing: '4px', color: '#fff' }}>SHOW RESULTS</div>
            <div style={{ ...S.mono, fontSize: '0.5rem', color: brandColor, letterSpacing: '2px', marginTop: '2px' }}>WEEK {week}</div>
          </div>
          {avgRating && (
            <div style={{ textAlign: 'center' }}>
              <StarRating value={parseFloat(avgRating)} onChange={() => {}} size={22} />
              <div style={{ ...S.mono, fontSize: '0.44rem', color: 'rgba(255,255,255,0.35)', marginTop: '4px', letterSpacing: '1px' }}>SHOW AVERAGE</div>
            </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 28px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

          {/* Left - All segments */}
          <div>
            <div style={{ ...S.label, marginBottom: '10px' }}>ALL SEGMENTS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {/* PLE Opener */}
              {isPLEWeek && renderPLESegment('OPENER', '#2ecc71', openerParts, pleCommonResults && pleCommonResults.opener, openerSlot && openerSlot.booking && openerSlot.booking.matchRule)}
              {slots.map((slot, i) => {
                // For PLE weeks: opener and main_event handled by renderPLESegment
                if (isPLEWeek && (slot.type === 'opener' || slot.type === 'main_event' || slot.isInterBrand)) return null;
                const result = results[i];
                const isPromo = slot.type === 'promo';
                const p = slot.booking && slot.booking.participants || [];
                const slotColor = isPromo ? '#9b59b6' : slot.type === 'main_event' ? brandColor : 'rgba(255,255,255,0.5)';
                const promoActionData = isPromo && slot.booking ? PROMO_ACTIONS.find(a => a.id === slot.booking.promoAction) : null;

                return (
                  <div key={i} style={{ padding: '8px 12px', borderRadius: '6px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ ...S.mono, fontSize: '0.38rem', color: slotColor, letterSpacing: '1px', marginBottom: '2px' }}>{SLOT_LABELS[slot.type]}</div>
                        {isPromo ? (
                          <div style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.6)' }}>
                            {promoActionData ? promoActionData.name : 'Promo'} — {p.map(w => w && getWrestlerName(w, 'display')).filter(Boolean).join(' & ')}
                          </div>
                        ) : (
                          <div>
                            <div style={{ ...S.mono, fontSize: '0.5rem' }}>
                              {renderMatchParticipants(p, result && result.matchRule || (slot.booking && slot.booking.matchRule), result && result.winner, S)}
                            </div>
                            {result && result.noContest && <span style={{ ...S.mono, fontSize: '0.46rem', color: '#e74c3c' }}> — NO CONTEST</span>}
                          </div>
                        )}
                      </div>
                      {result && result.rating > 0 && (
                        <div style={{ ...S.mono, fontSize: '0.55rem', color: RATING_COLORS[result.rating] || '#d4af37', flexShrink: 0 }}>{'★'.repeat(Math.floor(result.rating))}{(result.rating % 1 === 0.5) ? '½' : ''} {result.rating}</div>
                      )}
                    </div>
                    {result && result.notes && (
                      <div style={{ ...S.mono, fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)', marginTop: '4px', fontStyle: 'italic' }}>{result.notes}</div>
                    )}
                  </div>
                );
              })}
              {/* PLE Main Event */}
              {isPLEWeek && renderPLESegment('MAIN EVENT', brandColor, mainParts, pleCommonResults && pleCommonResults.main, mainSlot && mainSlot.booking && mainSlot.booking.matchRule)}
            </div>
          </div>

          {/* Right - Highlights */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* MOTN */}
            {motn && (
              <div style={{ ...S.panel, padding: '14px', borderColor: brandColor + '44', background: brandColor + '08' }}>
                <div style={{ ...S.label, marginBottom: '8px', color: brandColor }}>⭐ MATCH OF THE NIGHT</div>
                <div style={{ ...S.anton, fontSize: '0.75rem', letterSpacing: '1px', color: '#fff', marginBottom: '6px' }}>
                  {renderMatchParticipants(
                    (slots[motn.slotIdx] && slots[motn.slotIdx].booking && slots[motn.slotIdx].booking.participants) || [],
                    slots[motn.slotIdx] && slots[motn.slotIdx].booking && slots[motn.slotIdx].booking.matchRule,
                    motn.winner,
                    S
                  )}
                </div>
                <StarRating value={motn.rating} onChange={() => {}} size={20} />
              </div>
            )}

            {/* Big moments */}
            {/* PLE Winners */}
          {isPLEWeek && pleCommonResults && (pleCommonResults.opener || pleCommonResults.main) && (function() {
            var BRAND_LOGOS_W = {
              'Raw': process.env.PUBLIC_URL + '/brands/logos/RAW25_LOGO.png',
              'SmackDown': process.env.PUBLIC_URL + '/brands/logos/SMACKDOWN25_LOGO.png',
              'NXT': process.env.PUBLIC_URL + '/brands/logos/NXT_LOGO.png',
              'NXT 2.0': process.env.PUBLIC_URL + '/brands/logos/NXT20_LOGO.png',
              'WCW': process.env.PUBLIC_URL + '/brands/logos/WCW_LOGO.png',
              'ECW': process.env.PUBLIC_URL + '/brands/logos/ECW_LOGO.png',
              'NXT Mutiny': process.env.PUBLIC_URL + '/brands/logos/NXTMUTINY_LOGO.png',
            };
            var BRAND_COLORS_W = { 'Raw': '#c0392b', 'SmackDown': '#1a6bb5', 'NXT': '#a8a9ad', 'NXT 2.0': '#e91e8c', 'ECW': '#7b1fa2', 'WCW': '#e65100', 'NXT Mutiny': '#424242' };
            return (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ ...S.label, marginBottom: '8px' }}>🏆 PLE RESULTS</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {[
                  { key: 'opener', label: 'OPENER', color: '#2ecc71' },
                  { key: 'main', label: 'MAIN EVENT', color: brandColor }
                ].map(function(seg) {
                  var res = pleCommonResults[seg.key];
                  if (!res) return null;
                  var myWon = res.myWon;
                  var wTeam = res.winnerTeam || [];
                  // Fallback: build display from winnerBrand if no winnerTeam
                  var wBrand = res.winnerBrand;
                  var wColor = myWon ? seg.color : '#888';
                  // Pick color from first brand in winning team
                  if (wTeam.length > 0) {
                    var firstBrand = wTeam.find(function(p) { return p && p.isBrand; });
                    if (firstBrand) wColor = firstBrand.brandColor || BRAND_COLORS_W[firstBrand.brand_id] || '#888';
                  } else if (wBrand) {
                    wColor = BRAND_COLORS_W[wBrand] || '#888';
                  }
                  return (
                    <div key={seg.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '6px', background: wColor + '12', border: '1px solid ' + wColor + '44' }}>
                      <div style={{ ...S.mono, fontSize: '0.42rem', color: seg.color, letterSpacing: '1px', width: '80px', flexShrink: 0 }}>{seg.label}</div>
                      {/* Winner team logos/names */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: 1, flexWrap: 'wrap' }}>
                        {wTeam.length > 0 ? wTeam.map(function(p, pi) {
                          if (!p) return null;
                          var pBrand = p.isBrand ? p.brand_id : null;
                          var pLogo  = p.isBrand ? (p.brandLogo || BRAND_LOGOS_W[pBrand]) : (BRAND_LOGOS_W[(gameData && gameData.brand)]);
                          var pColor = p.isBrand ? (p.brandColor || BRAND_COLORS_W[pBrand] || '#888') : seg.color;
                          var pName  = p.isBrand ? pBrand : (p.name || getWrestlerName(p, 'display'));
                          return (
                            <React.Fragment key={pi}>
                              {pi > 0 && <span style={{ ...S.mono, fontSize: '0.4rem', color: 'rgba(255,255,255,0.3)' }}>&amp;</span>}
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: pColor + '18', border: '1px solid ' + pColor + '44', borderRadius: '3px', padding: '2px 8px' }}>
                                {pLogo && <img loading="lazy" src={pLogo} alt={pName} style={{ height: '14px', objectFit: 'contain', filter: 'drop-shadow(0 0 4px ' + pColor + '88)' }} onError={function(e) { e.target.style.display='none'; }} />}
                                {!pLogo && <span style={{ ...S.anton, fontSize: '0.52rem', color: pColor }}>{(pName || '?').toUpperCase()}</span>}
                              </span>
                            </React.Fragment>
                          );
                        }) : (wBrand ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            {BRAND_LOGOS_W[wBrand] && <img loading="lazy" src={BRAND_LOGOS_W[wBrand]} alt={wBrand} style={{ height: '18px', objectFit: 'contain', filter: 'drop-shadow(0 0 5px ' + wColor + '88)' }} onError={function(e) { e.target.style.display='none'; }} />}
                            {!BRAND_LOGOS_W[wBrand] && <span style={{ ...S.anton, fontSize: '0.55rem', color: wColor }}>{wBrand.toUpperCase()}</span>}
                          </span>
                        ) : null)}
                      </div>
                      <div style={{ ...S.mono, fontSize: '0.4rem', color: myWon ? '#2ecc71' : 'rgba(255,255,255,0.35)', flexShrink: 0 }}>
                        {myWon ? '✓ YOUR BRAND WON' : 'WON'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );})()}

          {(titleChanges.length > 0 || rivalryEvents.length > 0 || allInjuries.length > 0) && (
              <div style={{ ...S.panel, padding: '14px' }}>
                <div style={{ ...S.label, marginBottom: '10px' }}>🎯 BIG MOMENTS</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {titleChanges.map((r, i) => {
                    // Resolve proper title name
                    var champSlotsMM = (gameData && gameData.champions && gameData.champions.slots) || [];
                    var tcSlot = champSlotsMM.find(function(s) { return s.key === r.titleSlotKey; });
                    var tcTitleName = (tcSlot && tcSlot.titleOverride && tcSlot.titleOverride.name) || r.titleSlotKey || 'Championship';
                    var tcTitleRender = tcSlot && tcSlot.titleOverride && (tcSlot.titleOverride.render || getTitleRender(tcSlot.titleOverride.id));
                    // New champion(s): for tag, find the partner from participants
                    var tcNewChamp = r.newChampion;
                    var tcRoster = (gameData && gameData.roster) || [];
                    var tcTagTeams = (gameData && gameData.tagTeams) || [];
                    var tcIsTag = tcSlot && tcSlot.isTagTeam;
                    var tcChampNames = [];
                    if (tcIsTag) {
                      var tcTeam = tcTagTeams.find(function(t) {
                        return tcNewChamp && ((t.partner1 && String(t.partner1.wrestler_id) === String(tcNewChamp.wrestler_id)) ||
                               (t.partner2 && String(t.partner2.wrestler_id) === String(tcNewChamp.wrestler_id)));
                      });
                      if (tcTeam) {
                        var p1 = tcRoster.find(function(w) { return String(w.wrestler_id) === String(tcTeam.partner1.wrestler_id); }) || tcTeam.partner1;
                        var p2 = tcRoster.find(function(w) { return String(w.wrestler_id) === String(tcTeam.partner2.wrestler_id); }) || tcTeam.partner2;
                        tcChampNames = [getWrestlerName(p1, 'display'), getWrestlerName(p2, 'display')];
                      } else {
                        // fallback: use match participants same side
                        var tcParts = (r.participants || []).filter(Boolean);
                        var TC_TAG_RULE = { 'tag': [2,2], '3v3': [3,3], '4v4': [4,4], 'tag-triple-threat': [2,2,2], 'tag-fatal-4-way': [2,2,2,2], '2v2': [2,2] };
                        var tcSizes = TC_TAG_RULE[r.matchRule];
                        if (tcSizes && tcParts.length > 0) {
                          var tcOff = 0; var tcTeams = [];
                          tcSizes.forEach(function(sz) { tcTeams.push(tcParts.slice(tcOff, tcOff + sz)); tcOff += sz; });
                          var wSide = tcTeams.find(function(t) { return tcNewChamp && t.some(function(w) { return w && String(w.wrestler_id) === String(tcNewChamp.wrestler_id); }); });
                          if (wSide) tcChampNames = wSide.map(function(w) { return getWrestlerName(w, 'display'); });
                        }
                        if (tcChampNames.length === 0 && tcNewChamp) tcChampNames = [getWrestlerName(tcNewChamp, 'display')];
                      }
                    } else {
                      if (tcNewChamp) tcChampNames = [getWrestlerName(tcNewChamp, 'display')];
                    }
                    return (
                    <div key={'tc-' + i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '5px', background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)' }}>
                      <span style={{ fontSize: '0.7rem' }}>👑</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, flexWrap: 'wrap' }}>
                        {tcTitleRender && <img loading="lazy" src={tcTitleRender} alt="" style={{ height: '20px', objectFit: 'contain' }} onError={function(e) { e.target.style.display='none'; }} />}
                        <div>
                          <div style={{ ...S.mono, fontSize: '0.48rem', color: '#d4af37' }}>TITLE CHANGE</div>
                          <div style={{ ...S.mono, fontSize: '0.42rem', color: 'rgba(255,255,255,0.5)', marginTop: '1px' }}>
                            {tcChampNames.map(function(n) { return n && n.toUpperCase(); }).join(' & ')} wins the {tcTitleName}
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                  {allInjuries.map((inj, i) => (
                    <div key={'inj-' + i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '5px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)' }}>
                      <img loading="lazy" src={process.env.PUBLIC_URL + '/icons/injury.png'} alt="" style={{ width: '16px', height: '16px', objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />
                      <div style={{ ...S.mono, fontSize: '0.48rem', color: '#e74c3c' }}>INJURY</div>
                      <div style={{ ...S.mono, fontSize: '0.44rem', color: 'rgba(255,255,255,0.6)' }}>{inj.wrestler && inj.wrestler.name} — {inj.weeks} week{inj.weeks > 1 ? 's' : ''}</div>
                    </div>
                  ))}
                  {rivalryEvents.map((r, i) => {
                    const opt = RIVALRY_IMPACT_OPTIONS.find(o => o.id === r.rivalryImpact);
                    // Build side1 / side2 display
                    const side1Names = (r.rivalrySide1 || []).filter(Boolean).map(p => p.name || getWrestlerName(p, 'display') || '?');
                    const side2Names = (r.rivalrySide2 || []).filter(Boolean).map(p => p.name || getWrestlerName(p, 'display') || '?');
                    const callerNames = (r.callers || []).filter(Boolean).map(p => p.name || getWrestlerName(p, 'display') || '?');
                    const targetNames = (r.targets || []).filter(Boolean).map(p => p.name || getWrestlerName(p, 'display') || '?');
                    var displayLabel = '';
                    if (side1Names.length > 0 && side2Names.length > 0) {
                      displayLabel = side1Names.join(' & ') + ' vs ' + side2Names.join(' & ');
                    } else if (callerNames.length > 0 || targetNames.length > 0) {
                      displayLabel = callerNames.join(' & ') + (targetNames.length > 0 ? ' vs ' + targetNames.join(' & ') : '');
                    } else {
                      var allNames = (r.participants || []).filter(Boolean).map(p => p.name || getWrestlerName(p, 'display') || '?');
                      displayLabel = allNames.join(' & ');
                    }
                    return opt ? (
                      <div key={'rv-' + i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '5px', background: opt.color + '11', border: '1px solid ' + opt.color + '33', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.7rem' }}>{opt.label.split(' ')[0]}</span>
                        <div style={{ ...S.mono, fontSize: '0.48rem', color: opt.color }}>{opt.label.replace(opt.label.split(' ')[0] + ' ', '')}</div>
                        {displayLabel && (
                          <div style={{ ...S.mono, fontSize: '0.44rem', color: 'rgba(255,255,255,0.5)' }}>
                            — {displayLabel.toUpperCase()}
                          </div>
                        )}
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 28px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button className="sr-btn" onClick={() => setShowReopenConfirm(true)}
            style={{ ...S.mono, fontSize: '0.44rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.35)', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>
            🔓 REOPEN SHOW
          </button>
          <button className="sr-btn" onClick={onPostShow}
            style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '2px', background: brandColor + '22', border: '1px solid ' + brandColor, color: brandColor, padding: '10px 32px', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>
            POST-SHOW DATA →
          </button>
        </div>
      </div>

      {/* Reopen confirm */}
      {showReopenConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)' }}>
          <div style={{ ...S.panel, padding: '28px 32px', maxWidth: '380px', textAlign: 'center' }}>
            <div style={{ ...S.anton, fontSize: '1rem', color: '#e74c3c', letterSpacing: '2px', marginBottom: '10px' }}>⚠ REOPEN SHOW?</div>
            <div style={{ ...S.mono, fontSize: '0.52rem', color: 'rgba(255,255,255,0.4)', marginBottom: '20px', lineHeight: '1.5' }}>This will discard all results entered so far. This action cannot be undone.</div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={() => setShowReopenConfirm(false)} style={{ ...S.mono, fontSize: '0.5rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.5)', padding: '8px 20px', borderRadius: '4px', cursor: 'pointer' }}>CANCEL</button>
              <button onClick={() => { setShowReopenConfirm(false); onReopen(); }} style={{ ...S.mono, fontSize: '0.5rem', background: 'rgba(231,76,60,0.12)', border: '1px solid rgba(231,76,60,0.5)', color: '#e74c3c', padding: '8px 20px', borderRadius: '4px', cursor: 'pointer' }}>CONFIRM</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── STEP 4: Post-Show Data ───────────────────────────────────────────
export const PostShowData = ({ gameData, results, brandColor, brandBg, onAdvanceWeek }) => {
  const week = (gameData && gameData.week) || 1;
  const rivals = ((gameData && gameData.rivals) || []).filter(Boolean);
  const currentBudget = (gameData && gameData.budget) || 0;
  const currentFans = (gameData && gameData.fans) || 0;
  const showCost = ((gameData && gameData.bookingSlots) || []).reduce((t, s) => t + (s.booking && s.booking.cost || 0), 0);

  const [newBudget, setNewBudget] = useState('');
  const [newFans, setNewFans] = useState('');
  const [rivalFansNew, setRivalFansNew] = useState(rivals.map(() => ''));

  const newBudgetNum = Number(newBudget) || 0;
  const newFansNum = Number(newFans) || 0;
  const budgetDiff = newBudget !== '' ? newBudgetNum - currentBudget : null;
  const fansDiff = newFans !== '' ? newFansNum - currentFans : null;
  const canAdvance = newBudget !== '' && newFans !== '';

  const BRAND_LOGOS = {
    'Raw': process.env.PUBLIC_URL + '/brands/logos/RAW25_LOGO.png',
    'SmackDown': process.env.PUBLIC_URL + '/brands/logos/SMACKDOWN25_LOGO.png',
    'NXT': process.env.PUBLIC_URL + '/brands/logos/NXT_LOGO.png',
    'NXT 2.0': process.env.PUBLIC_URL + '/brands/logos/NXT20_LOGO.png',
    'WCW': process.env.PUBLIC_URL + '/brands/logos/WCW_LOGO.png',
    'ECW': process.env.PUBLIC_URL + '/brands/logos/ECW_LOGO.png',
    'NXT Mutiny': process.env.PUBLIC_URL + '/brands/logos/NXTMUTINY_LOGO.png',
  };
  const BRAND_COLORS_PSD = { 'Raw': '#c0392b', 'SmackDown': '#1a6bb5', 'NXT': '#a8a9ad', 'NXT 2.0': '#e91e8c', 'ECW': '#7b1fa2', 'WCW': '#e65100', 'NXT Mutiny': '#424242' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '91px', paddingBottom: '16px', background: 'rgba(0,0,0,0.95)' }}>
      <style>{css}</style>
      {brandBg && <img loading="lazy" src={brandBg} alt="" style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(18px) brightness(0.18)', zIndex: 0 }} />}

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '680px', margin: '0 16px', ...S.panel, overflow: 'hidden', maxHeight: 'calc(100vh - 107px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ height: '4px', background: 'linear-gradient(90deg, transparent, ' + brandColor + ', transparent)' }} />

        <div style={{ padding: '20px 28px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ ...S.anton, fontSize: '1.2rem', letterSpacing: '4px', color: '#fff' }}>POST-SHOW DATA</div>
          <div style={{ ...S.mono, fontSize: '0.5rem', color: '#e67e22', marginTop: '4px', letterSpacing: '1px' }}>⚠ Required before advancing to next week</div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 28px', display: 'flex', flexDirection: 'column', gap: '18px' }}>

          {/* BUDGET */}
          <div style={{ ...S.panel, padding: '16px 18px' }}>
            <div style={{ ...S.anton, fontSize: '0.8rem', letterSpacing: '2px', color: '#2ecc71', marginBottom: '12px' }}>💰 BUDGET</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { label: 'Opening Balance', value: '$' + currentBudget.toLocaleString('en-US'), color: '#fff' },
                { label: 'Show Cost', value: '-$' + showCost.toLocaleString('en-US'), color: 'rgba(231,76,60,0.7)' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ ...S.label }}>{label}</span>
                  <span style={{ ...S.mono, fontSize: '0.6rem', color }}>{value}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '4px' }}>
                <span style={{ ...S.label }}>New Budget</span>
                <input type="number" value={newBudget} onChange={e => setNewBudget(e.target.value)} placeholder="Enter new total..."
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', padding: '5px 10px', color: '#fff', fontSize: '0.6rem', fontFamily: "'Monda',sans-serif", outline: 'none', width: '160px', textAlign: 'right' }} />
              </div>
              {newBudget !== '' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: '6px', background: newBudgetNum >= 0 ? 'rgba(46,204,113,0.08)' : 'rgba(231,76,60,0.08)', border: '1px solid ' + (newBudgetNum >= 0 ? 'rgba(46,204,113,0.3)' : 'rgba(231,76,60,0.3)') }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ ...S.anton, fontSize: '0.6rem', letterSpacing: '1px', color: newBudgetNum >= 0 ? '#2ecc71' : '#e74c3c' }}>NEW BUDGET</span>
                    {budgetDiff !== null && (
                      <span style={{ ...S.mono, fontSize: '0.42rem', color: budgetDiff >= 0 ? '#2ecc71' : '#e74c3c' }}>
                        {budgetDiff >= 0 ? '+' : ''}{budgetDiff.toLocaleString('en-US')} vs last week
                      </span>
                    )}
                  </div>
                  <span style={{ ...S.anton, fontSize: '1rem', color: newBudgetNum >= 0 ? '#2ecc71' : '#e74c3c' }}>${newBudgetNum.toLocaleString('en-US')}</span>
                </div>
              )}
            </div>
          </div>

          {/* FANS */}
          <div style={{ ...S.panel, padding: '16px 18px' }}>
            <div style={{ ...S.anton, fontSize: '0.8rem', letterSpacing: '2px', color: '#3498db', marginBottom: '12px' }}>👥 FANS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ ...S.label }}>Current Fans</span>
                <span style={{ ...S.mono, fontSize: '0.6rem', color: '#fff' }}>{currentFans.toLocaleString('en-US')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '4px' }}>
                <span style={{ ...S.label }}>New Fans</span>
                <input type="number" value={newFans} onChange={e => setNewFans(e.target.value)} placeholder="Enter new total..."
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', padding: '5px 10px', color: '#fff', fontSize: '0.6rem', fontFamily: "'Monda',sans-serif", outline: 'none', width: '160px', textAlign: 'right' }} />
              </div>
              {newFans !== '' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: '6px', background: 'rgba(52,152,219,0.08)', border: '1px solid rgba(52,152,219,0.3)' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ ...S.anton, fontSize: '0.6rem', letterSpacing: '1px', color: '#3498db' }}>NEW FANS</span>
                    {fansDiff !== null && (
                      <span style={{ ...S.mono, fontSize: '0.42rem', color: fansDiff >= 0 ? '#2ecc71' : '#e74c3c' }}>
                        {fansDiff >= 0 ? '+' : ''}{fansDiff.toLocaleString('en-US')} vs last week
                      </span>
                    )}
                  </div>
                  <span style={{ ...S.anton, fontSize: '1rem', color: '#3498db' }}>{newFansNum.toLocaleString('en-US')}</span>
                </div>
              )}
            </div>
          </div>

          {/* RIVALS */}
          {rivals.length > 0 && (
            <div style={{ ...S.panel, padding: '16px 18px' }}>
              <div style={{ ...S.anton, fontSize: '0.8rem', letterSpacing: '2px', color: '#e74c3c', marginBottom: '12px' }}>⚔️ RIVAL FANS</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {rivals.map((r, i) => {
                  const baseFans = r.fans || 0;
                  const newTotal = Number(rivalFansNew[i]) || 0;
                  const diff = rivalFansNew[i] !== '' ? newTotal - baseFans : null;
                  return (
                    <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {(BRAND_LOGOS[r.brand] || r.brandLogo) && <img loading="lazy" src={BRAND_LOGOS[r.brand] || r.brandLogo} alt="" style={{ height: '22px', objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />}
                          <div>
                            <div style={{ ...S.anton, fontSize: '0.6rem', letterSpacing: '1.5px', color: BRAND_COLORS_PSD[r.brand] || r.brandColor || '#fff', lineHeight: 1 }}>{(r.brand || '').toUpperCase()}</div>
                            <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.6)', marginTop: '2px' }}>{r.gm}</div>
                          </div>
                        </div>
                        <span style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.4)' }}>Current: {baseFans.toLocaleString('en-US')}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                        <span style={{ ...S.label }}>New Fans</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input type="number" value={rivalFansNew[i]} onChange={e => { const n = [...rivalFansNew]; n[i] = e.target.value; setRivalFansNew(n); }}
                            placeholder="New total..."
                            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', padding: '5px 10px', color: '#fff', fontSize: '0.6rem', fontFamily: "'Monda',sans-serif", outline: 'none', width: '110px', textAlign: 'right' }} />
                          {rivalFansNew[i] !== '' && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: '80px' }}>
                              <span style={{ ...S.anton, fontSize: '0.7rem', color: '#3498db' }}>{newTotal.toLocaleString('en-US')}</span>
                              {diff !== null && <span style={{ ...S.mono, fontSize: '0.38rem', color: diff >= 0 ? '#2ecc71' : '#e74c3c' }}>{diff >= 0 ? '+' : ''}{diff.toLocaleString('en-US')}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 28px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="sr-btn" onClick={() => canAdvance && onAdvanceWeek({ finalBudget: newBudgetNum, finalFans: newFansNum, rivalFans: rivals.map((r, i) => ({ ...r, fans: rivalFansNew[i] !== '' ? Number(rivalFansNew[i]) : (r.fans || 0) })) })}
            disabled={!canAdvance}
            style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '2px', background: canAdvance ? brandColor + '22' : 'transparent', border: '1px solid ' + (canAdvance ? brandColor : 'rgba(255,255,255,0.12)'), color: canAdvance ? brandColor : 'rgba(255,255,255,0.2)', padding: '10px 32px', borderRadius: '4px', cursor: canAdvance ? 'pointer' : 'not-allowed', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>
            ▶ ADVANCE TO WEEK {week + 1}
          </button>
        </div>
      </div>
    </div>
  );
};
